import React, { useState, useRef, useCallback, useEffect } from 'react';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { useToast } from '../ToastContext';
import { fmtTokens } from '../../utils/helpers';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const NUM_REELS = 5;
const NUM_ROWS = 3;
const NUM_PAYLINES = 20;

const SYMBOLS = [
  { id: 'wild',       name: 'Wild',           color: '#FFD700' },
  { id: 'impostor',   name: 'Impostor',       color: '#E8384A' },
  { id: 'crewmate',   name: 'Crewmate',       color: '#3A8FD4' },
  { id: 'mini',       name: 'Reactor',        color: '#00E5FF' },
  { id: 'bone',       name: 'Bone',           color: '#BDBDBD' },
  { id: 'oxygen',     name: 'O2',             color: '#66BB6A' },
  { id: 'electrical', name: 'Electrical',     color: '#FDD835' },
  { id: 'emergency',  name: 'Emergency',      color: '#FF5722' },
  { id: 'vent',       name: 'Vent',           color: '#795548' },
  { id: 'task',       name: 'Task',           color: '#26A69A' },
];

const PAYLINES = [
  [1,1,1,1,1], [0,0,0,0,0], [2,2,2,2,2],
  [0,1,2,1,0], [2,1,0,1,2],
  [0,0,1,2,2], [2,2,1,0,0],
  [1,0,0,0,1], [1,2,2,2,1],
  [0,1,1,1,0], [2,1,1,1,2],
  [1,0,1,0,1], [1,2,1,2,1],
  [0,1,0,1,0], [2,1,2,1,2],
  [0,0,1,0,0], [2,2,1,2,2],
  [1,0,0,1,2], [1,2,2,1,0],
  [0,1,2,2,1],
];

const PAYLINE_COLORS = [
  '#FF1744', '#2979FF', '#00E676', '#FFD600', '#D500F9',
  '#FF9100', '#00B8D4', '#F50057', '#76FF03', '#651FFF',
  '#FF3D00', '#1DE9B6', '#C6FF00', '#FFAB40', '#40C4FF',
  '#FF6E40', '#69F0AE', '#EEFF41', '#EA80FC', '#84FFFF',
];

const PAYTABLE = {
  wild:       { 5: 2000, 4: 200, 3: 25   },
  impostor:   { 5: 5000, 4: 500, 3: 50   },
  crewmate:   { 5: 500,  4: 100, 3: 15.5 },
  mini:       { 5: 200,  4: 40,  3: 8.5  },
  electrical: { 5: 150,  4: 30,  3: 5.5  },
  oxygen:     { 5: 100,  4: 20,  3: 3.5  },
  bone:       { 5: 60,   4: 10,  3: 2.4  },
};

const SCATTER_SYMBOLS = new Set(['emergency', 'vent', 'task']);

// ─────────────────────────────────────────────────────────────────────────────
// WEB AUDIO — synthesised Among Us sounds
// ─────────────────────────────────────────────────────────────────────────────
class SlotAudio {
  constructor() {
    this.ctx = null;
    this.muted = false;
  }

  init() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.ctx.state === 'suspended') this.ctx.resume();
  }

  // Short click for each reel tick
  tick() {
    if (this.muted || !this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'square';
    o.frequency.value = 800 + Math.random() * 400;
    g.gain.setValueAtTime(0.04, this.ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.03);
    o.connect(g).connect(this.ctx.destination);
    o.start(); o.stop(this.ctx.currentTime + 0.03);
  }

  // Thud when reel stops
  reelStop() {
    if (this.muted || !this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'sine';
    o.frequency.value = 120;
    o.frequency.exponentialRampToValueAtTime(40, this.ctx.currentTime + 0.15);
    g.gain.setValueAtTime(0.2, this.ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.15);
    o.connect(g).connect(this.ctx.destination);
    o.start(); o.stop(this.ctx.currentTime + 0.15);
  }

  // Spinning reel noise (filtered noise burst)
  spinStart() {
    if (this.muted || !this.ctx) return;
    const bufferSize = this.ctx.sampleRate * 0.5;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const filt = this.ctx.createBiquadFilter();
    filt.type = 'bandpass'; filt.frequency.value = 2000; filt.Q.value = 2;
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.06, this.ctx.currentTime);
    g.gain.linearRampToValueAtTime(0, this.ctx.currentTime + 0.5);
    src.connect(filt).connect(g).connect(this.ctx.destination);
    src.start(); src.stop(this.ctx.currentTime + 0.5);
  }

  // Win jingle — ascending notes
  win(big = false) {
    if (this.muted || !this.ctx) return;
    const notes = big ? [523, 659, 784, 1047, 1319] : [523, 659, 784];
    const now = this.ctx.currentTime;
    notes.forEach((freq, i) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = big ? 'sawtooth' : 'triangle';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0, now + i * 0.12);
      g.gain.linearRampToValueAtTime(big ? 0.12 : 0.08, now + i * 0.12 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.3);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.12); o.stop(now + i * 0.12 + 0.3);
    });
  }

  // Emergency meeting alarm
  emergencyAlarm() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    for (let i = 0; i < 6; i++) {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = 'sawtooth';
      o.frequency.value = i % 2 === 0 ? 880 : 660;
      g.gain.setValueAtTime(0.15, now + i * 0.12);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.12 + 0.1);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.12); o.stop(now + i * 0.12 + 0.1);
    }
  }

  // Vent whoosh
  ventSound() {
    if (this.muted || !this.ctx) return;
    const bufferSize = this.ctx.sampleRate * 0.4;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const filt = this.ctx.createBiquadFilter();
    filt.type = 'lowpass';
    filt.frequency.setValueAtTime(3000, this.ctx.currentTime);
    filt.frequency.exponentialRampToValueAtTime(200, this.ctx.currentTime + 0.4);
    const g = this.ctx.createGain();
    g.gain.setValueAtTime(0.2, this.ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.4);
    src.connect(filt).connect(g).connect(this.ctx.destination);
    src.start(); src.stop(this.ctx.currentTime + 0.4);
  }

  // Task complete chime
  taskChime() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    [392, 523, 659, 784].forEach((freq, i) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = 'sine';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.1, now + i * 0.1);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.1 + 0.25);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.1); o.stop(now + i * 0.1 + 0.25);
    });
  }

  // Wheel tick
  wheelTick() {
    if (this.muted || !this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'sine';
    o.frequency.value = 1200;
    g.gain.setValueAtTime(0.06, this.ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.04);
    o.connect(g).connect(this.ctx.destination);
    o.start(); o.stop(this.ctx.currentTime + 0.04);
  }

  // Jackpot / mega win
  jackpot() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    const melody = [523, 587, 659, 698, 784, 880, 988, 1047];
    melody.forEach((freq, i) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = 'sawtooth';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.1, now + i * 0.08);
      g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.08 + 0.25);
      o.connect(g).connect(this.ctx.destination);
      o.start(now + i * 0.08); o.stop(now + i * 0.08 + 0.25);
    });
  }

  // Epic win — big fanfare with harmonics for 100x+
  epicWin() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    // Rising chord arpeggio
    const chords = [
      [523, 659, 784],   // C major
      [587, 740, 880],   // D major
      [659, 831, 988],   // E major
      [698, 880, 1047],  // F major
      [784, 988, 1175],  // G major
      [880, 1109, 1319], // A major
      [988, 1245, 1480], // B major
      [1047, 1319, 1568] // C major (octave up)
    ];
    chords.forEach((chord, ci) => {
      chord.forEach((freq, fi) => {
        const o = this.ctx.createOscillator();
        const g = this.ctx.createGain();
        o.type = fi === 0 ? 'sawtooth' : 'triangle';
        o.frequency.value = freq;
        const t = now + ci * 0.1;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(0.08, t + 0.03);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
        o.connect(g).connect(this.ctx.destination);
        o.start(t); o.stop(t + 0.5);
      });
    });
    // Sustaining low bass note
    const bass = this.ctx.createOscillator();
    const bg = this.ctx.createGain();
    bass.type = 'sine';
    bass.frequency.value = 131;
    bg.gain.setValueAtTime(0.15, now);
    bg.gain.linearRampToValueAtTime(0.15, now + 0.8);
    bg.gain.exponentialRampToValueAtTime(0.001, now + 1.5);
    bass.connect(bg).connect(this.ctx.destination);
    bass.start(now); bass.stop(now + 1.5);
  }

  // Mega win — dramatic for 50x+
  megaWin() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    // Rapid ascending scale with harmonics
    const notes = [262, 330, 392, 523, 659, 784, 1047, 1319, 1568];
    notes.forEach((freq, i) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = 'sawtooth';
      o.frequency.value = freq;
      const t = now + i * 0.07;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.1, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      o.connect(g).connect(this.ctx.destination);
      o.start(t); o.stop(t + 0.35);
    });
    // Cymbal-like noise burst
    const bufferSize = this.ctx.sampleRate * 0.8;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const filt = this.ctx.createBiquadFilter();
    filt.type = 'highpass'; filt.frequency.value = 6000;
    const ng = this.ctx.createGain();
    ng.gain.setValueAtTime(0.06, now + 0.3);
    ng.gain.exponentialRampToValueAtTime(0.001, now + 1.0);
    src.connect(filt).connect(ng).connect(this.ctx.destination);
    src.start(now + 0.3); src.stop(now + 1.0);
  }

  // Big win — for 25x+
  bigWin() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    const notes = [523, 659, 784, 1047, 784, 1047, 1319];
    notes.forEach((freq, i) => {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = i % 2 === 0 ? 'sawtooth' : 'triangle';
      o.frequency.value = freq;
      const t = now + i * 0.09;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.1, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
      o.connect(g).connect(this.ctx.destination);
      o.start(t); o.stop(t + 0.3);
    });
  }

  // Lose sound
  lose() {
    if (this.muted || !this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'triangle';
    o.frequency.setValueAtTime(300, this.ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(100, this.ctx.currentTime + 0.4);
    g.gain.setValueAtTime(0.06, this.ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.4);
    o.connect(g).connect(this.ctx.destination);
    o.start(); o.stop(this.ctx.currentTime + 0.4);
  }

  // Bonus symbol landing — shimmery impact
  bonusLand() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    // Impact thud
    const thud = this.ctx.createOscillator();
    const tg = this.ctx.createGain();
    thud.type = 'sine';
    thud.frequency.setValueAtTime(200, now);
    thud.frequency.exponentialRampToValueAtTime(60, now + 0.15);
    tg.gain.setValueAtTime(0.25, now);
    tg.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    thud.connect(tg).connect(this.ctx.destination);
    thud.start(now); thud.stop(now + 0.15);
    // Shimmer overtone
    const shim = this.ctx.createOscillator();
    const sg = this.ctx.createGain();
    shim.type = 'triangle';
    shim.frequency.value = 1800;
    sg.gain.setValueAtTime(0.06, now);
    sg.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    shim.connect(sg).connect(this.ctx.destination);
    shim.start(now); shim.stop(now + 0.3);
  }

  // Teaser suspense — heartbeat-like building tension when 2 bonuses landed
  bonusTeaser() {
    if (this.muted || !this.ctx) return;
    const now = this.ctx.currentTime;
    // Deep heartbeat pulses
    for (let i = 0; i < 6; i++) {
      const o = this.ctx.createOscillator();
      const g = this.ctx.createGain();
      o.type = 'sine';
      o.frequency.value = 55 + i * 5;
      const t = now + i * 0.25;
      g.gain.setValueAtTime(0.2, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      o.connect(g).connect(this.ctx.destination);
      o.start(t); o.stop(t + 0.2);
    }
    // Rising tension tone
    const rise = this.ctx.createOscillator();
    const rg = this.ctx.createGain();
    rise.type = 'sawtooth';
    rise.frequency.setValueAtTime(200, now);
    rise.frequency.exponentialRampToValueAtTime(800, now + 1.5);
    rg.gain.setValueAtTime(0.04, now);
    rg.gain.linearRampToValueAtTime(0.1, now + 1.2);
    rg.gain.exponentialRampToValueAtTime(0.001, now + 1.5);
    rise.connect(rg).connect(this.ctx.destination);
    rise.start(now); rise.stop(now + 1.5);
  }

  // Reel tick that slows down (for teaser reels) — deeper, more deliberate
  teaserTick() {
    if (this.muted || !this.ctx) return;
    const o = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    o.type = 'triangle';
    o.frequency.value = 400 + Math.random() * 200;
    g.gain.setValueAtTime(0.08, this.ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.06);
    o.connect(g).connect(this.ctx.destination);
    o.start(); o.stop(this.ctx.currentTime + 0.06);
  }
}

const audio = new SlotAudio();

// ─────────────────────────────────────────────────────────────────────────────
// SVG SYMBOL RENDERERS — Among Us themed
// ─────────────────────────────────────────────────────────────────────────────
function CrewmateSVG({ color = '#3A8FD4', size = 48, mini = false, knife = false, glow = false }) {
  const s = mini ? size * 0.7 : size;
  const glowId = `glow-${color.replace('#', '')}-${Math.random().toString(36).slice(2, 6)}`;
  return (
    <svg width={s} height={s} viewBox="0 0 64 64" style={{ filter: glow ? `drop-shadow(0 0 6px ${color})` : 'none' }}>
      {glow && (
        <defs>
          <filter id={glowId}>
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
      )}
      <g filter={glow ? `url(#${glowId})` : undefined}>
        {/* Body */}
        <ellipse cx="32" cy="40" rx="16" ry="18" fill={color} />
        {/* Visor */}
        <ellipse cx="38" cy="30" rx="10" ry="8" fill="#C5E8FF" opacity="0.9" />
        <ellipse cx="39" cy="29" rx="7" ry="5.5" fill="#8DD4FF" />
        {/* Backpack */}
        <rect x="12" y="30" width="6" height="16" rx="3" fill={color} opacity="0.8" />
        {/* Legs */}
        <ellipse cx="25" cy="57" rx="6" ry="5" fill={color} />
        <ellipse cx="39" cy="57" rx="6" ry="5" fill={color} />
        {/* Shadow/depth on body */}
        <ellipse cx="32" cy="42" rx="14" ry="15" fill="rgba(0,0,0,0.15)" />
        <ellipse cx="32" cy="38" rx="14" ry="16" fill={color} />
        {/* Visor shine */}
        <ellipse cx="36" cy="27" rx="3" ry="2" fill="rgba(255,255,255,0.5)" />
        {knife && (
          <g transform="translate(44, 18) rotate(30)">
            <rect x="0" y="0" width="3" height="16" rx="1" fill="#AAA" />
            <rect x="-1" y="14" width="5" height="6" rx="1" fill="#654321" />
            <polygon points="0,0 3,0 1.5,-6" fill="#CCC" />
          </g>
        )}
      </g>
    </svg>
  );
}

function BoneSVG({ size = 48, glow = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ filter: glow ? 'drop-shadow(0 0 6px #BDBDBD)' : 'none' }}>
      <g transform="translate(32,32) rotate(-30)">
        <rect x="-3" y="-18" width="6" height="36" rx="3" fill="#E0E0E0" />
        <circle cx="-5" cy="-18" r="5" fill="#E0E0E0" />
        <circle cx="5" cy="-18" r="5" fill="#E0E0E0" />
        <circle cx="-5" cy="18" r="5" fill="#E0E0E0" />
        <circle cx="5" cy="18" r="5" fill="#E0E0E0" />
      </g>
    </svg>
  );
}

function EmergencyButtonSVG({ size = 48, glow = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ filter: glow ? 'drop-shadow(0 0 8px #FF5722) drop-shadow(0 0 16px #FF5722)' : 'none' }}>
      {/* Base */}
      <ellipse cx="32" cy="42" rx="24" ry="12" fill="#555" />
      <ellipse cx="32" cy="40" rx="24" ry="12" fill="#777" />
      {/* Button dome */}
      <ellipse cx="32" cy="32" rx="16" ry="14" fill="#D32F2F" />
      <ellipse cx="32" cy="30" rx="14" ry="11" fill="#F44336" />
      <ellipse cx="30" cy="27" rx="6" ry="4" fill="rgba(255,255,255,0.3)" />
      {/* Exclamation */}
      <rect x="30" y="22" width="4" height="10" rx="2" fill="#FFF" opacity="0.9" />
      <circle cx="32" cy="36" r="2" fill="#FFF" opacity="0.9" />
    </svg>
  );
}

function VentSVG({ size = 48, glow = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ filter: glow ? 'drop-shadow(0 0 8px #795548) drop-shadow(0 0 16px #795548)' : 'none' }}>
      <rect x="8" y="20" width="48" height="30" rx="4" fill="#5D4037" />
      <rect x="10" y="22" width="44" height="26" rx="3" fill="#795548" />
      {/* Slats */}
      {[0, 1, 2, 3, 4].map(i => (
        <rect key={i} x="14" y={24 + i * 5} width="36" height="2.5" rx="1" fill="#4E342E" />
      ))}
      {/* Screws */}
      <circle cx="13" cy="25" r="1.5" fill="#8D6E63" />
      <circle cx="51" cy="25" r="1.5" fill="#8D6E63" />
      <circle cx="13" cy="45" r="1.5" fill="#8D6E63" />
      <circle cx="51" cy="45" r="1.5" fill="#8D6E63" />
    </svg>
  );
}

function TaskSVG({ size = 48, glow = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ filter: glow ? 'drop-shadow(0 0 8px #26A69A) drop-shadow(0 0 16px #26A69A)' : 'none' }}>
      {/* Clipboard */}
      <rect x="14" y="12" width="36" height="44" rx="3" fill="#26A69A" />
      <rect x="16" y="16" width="32" height="38" rx="2" fill="#E0F2F1" />
      {/* Clip */}
      <rect x="24" y="8" width="16" height="10" rx="3" fill="#00897B" />
      <rect x="27" y="6" width="10" height="6" rx="2" fill="#B2DFDB" />
      {/* Check lines */}
      <rect x="20" y="22" width="20" height="2" rx="1" fill="#80CBC4" />
      <rect x="20" y="28" width="24" height="2" rx="1" fill="#80CBC4" />
      <rect x="20" y="34" width="16" height="2" rx="1" fill="#80CBC4" />
      {/* Checkmark */}
      <polyline points="34,38 38,44 46,32" fill="none" stroke="#00C853" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function OxygenSVG({ size = 48, glow = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ filter: glow ? 'drop-shadow(0 0 6px #66BB6A)' : 'none' }}>
      <circle cx="32" cy="32" r="22" fill="none" stroke="#66BB6A" strokeWidth="4" />
      <text x="32" y="33" textAnchor="middle" dominantBaseline="central" fill="#66BB6A"
        style={{ fontSize: '18px', fontWeight: '900', fontFamily: 'Orbitron, monospace' }}>
        O₂
      </text>
      {/* Bubbles */}
      <circle cx="46" cy="16" r="3" fill="#A5D6A7" opacity="0.6" />
      <circle cx="50" cy="22" r="2" fill="#A5D6A7" opacity="0.4" />
    </svg>
  );
}

function ElectricalSVG({ size = 48, glow = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ filter: glow ? 'drop-shadow(0 0 6px #FDD835)' : 'none' }}>
      <polygon points="30,8 20,34 30,34 24,56 44,28 34,28 40,8" fill="#FDD835" />
      <polygon points="31,14 24,32 31,32 27,50 40,30 34,30 38,14" fill="#FFF176" />
    </svg>
  );
}

function RainbowCrewmateSVG({ size = 48, glow = false }) {
  const id = `rainbow-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ filter: glow ? 'drop-shadow(0 0 8px #FFD700) drop-shadow(0 0 14px #FF5722)' : 'drop-shadow(0 0 4px rgba(255,215,0,0.4))' }}>
      <defs>
        <linearGradient id={`rb-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FF1744" />
          <stop offset="20%" stopColor="#FF9100" />
          <stop offset="40%" stopColor="#FFD600" />
          <stop offset="60%" stopColor="#00E676" />
          <stop offset="80%" stopColor="#2979FF" />
          <stop offset="100%" stopColor="#D500F9" />
        </linearGradient>
        <linearGradient id={`rb2-${id}`} x1="100%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#FF1744" />
          <stop offset="25%" stopColor="#FFD600" />
          <stop offset="50%" stopColor="#00E676" />
          <stop offset="75%" stopColor="#2979FF" />
          <stop offset="100%" stopColor="#D500F9" />
        </linearGradient>
      </defs>
      {/* Body */}
      <ellipse cx="32" cy="40" rx="16" ry="18" fill={`url(#rb-${id})`} />
      {/* Visor */}
      <ellipse cx="38" cy="30" rx="10" ry="8" fill="#C5E8FF" opacity="0.9" />
      <ellipse cx="39" cy="29" rx="7" ry="5.5" fill="#8DD4FF" />
      {/* Backpack */}
      <rect x="12" y="30" width="6" height="16" rx="3" fill={`url(#rb2-${id})`} opacity="0.8" />
      {/* Legs */}
      <ellipse cx="25" cy="57" rx="6" ry="5" fill={`url(#rb-${id})`} />
      <ellipse cx="39" cy="57" rx="6" ry="5" fill={`url(#rb2-${id})`} />
      {/* Shadow/depth on body */}
      <ellipse cx="32" cy="42" rx="14" ry="15" fill="rgba(0,0,0,0.12)" />
      <ellipse cx="32" cy="38" rx="14" ry="16" fill={`url(#rb-${id})`} />
      {/* Visor shine */}
      <ellipse cx="36" cy="27" rx="3" ry="2" fill="rgba(255,255,255,0.5)" />
      {/* Star sparkle */}
      <polygon points="52,12 53.5,16 58,16 54.5,18.5 56,22.5 52,20 48,22.5 49.5,18.5 46,16 50.5,16" fill="#FFD700" opacity="0.9" />
      <polygon points="10,14 11,16.5 13.5,16.5 11.5,18 12.5,20.5 10,19 7.5,20.5 8.5,18 6.5,16.5 9,16.5" fill="#FFD700" opacity="0.7" />
      {/* "W" badge */}
      <text x="32" y="44" textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize="10" fontWeight="900"
        style={{ fontFamily: 'Orbitron, monospace', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>W</text>
    </svg>
  );
}

function ReactorSVG({ size = 48, glow = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" style={{ filter: glow ? 'drop-shadow(0 0 8px #00E5FF) drop-shadow(0 0 16px #00E5FF)' : 'none' }}>
      {/* Containment ring */}
      <circle cx="32" cy="32" r="26" fill="none" stroke="#006064" strokeWidth="4" />
      <circle cx="32" cy="32" r="23" fill="none" stroke="#00838F" strokeWidth="1.5" />
      {/* Inner glow */}
      <circle cx="32" cy="32" r="18" fill="rgba(0,229,255,0.1)" />
      <circle cx="32" cy="32" r="12" fill="rgba(0,229,255,0.2)" />
      {/* Core */}
      <circle cx="32" cy="32" r="8" fill="#00E5FF" opacity="0.9" />
      <circle cx="32" cy="32" r="5" fill="#B2EBF2" />
      <circle cx="30" cy="30" r="2" fill="rgba(255,255,255,0.7)" />
      {/* Energy arcs */}
      <path d="M32 6 L32 14" stroke="#00E5FF" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
      <path d="M32 50 L32 58" stroke="#00E5FF" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
      <path d="M6 32 L14 32" stroke="#00E5FF" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
      <path d="M50 32 L58 32" stroke="#00E5FF" strokeWidth="2" strokeLinecap="round" opacity="0.7" />
      {/* Diagonal energy */}
      <path d="M13 13 L19 19" stroke="#00BCD4" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
      <path d="M45 45 L51 51" stroke="#00BCD4" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
      <path d="M51 13 L45 19" stroke="#00BCD4" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
      <path d="M19 45 L13 51" stroke="#00BCD4" strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
      {/* Warning segments on ring */}
      <path d="M 32 6 A 26 26 0 0 1 50 12" fill="none" stroke="#FFD600" strokeWidth="2" opacity="0.6" />
      <path d="M 32 58 A 26 26 0 0 1 14 52" fill="none" stroke="#FFD600" strokeWidth="2" opacity="0.6" />
    </svg>
  );
}

function renderSymbol(symbolId, size = 48, glow = false) {
  switch (symbolId) {
    case 'wild':       return <RainbowCrewmateSVG size={size} glow={glow} />;
    case 'impostor':   return <CrewmateSVG color="#E8384A" size={size} glow={glow} />;
    case 'crewmate':   return <CrewmateSVG color="#3A8FD4" size={size} glow={glow} />;
    case 'mini':       return <ReactorSVG size={size} glow={glow} />;
    case 'bone':       return <BoneSVG size={size} glow={glow} />;
    case 'emergency':  return <EmergencyButtonSVG size={size} glow={glow} />;
    case 'vent':       return <VentSVG size={size} glow={glow} />;
    case 'task':       return <TaskSVG size={size} glow={glow} />;
    case 'oxygen':     return <OxygenSVG size={size} glow={glow} />;
    case 'electrical': return <ElectricalSVG size={size} glow={glow} />;
    default:           return <CrewmateSVG color="#888" size={size} />;
  }
}

const BONUS_BUY_OPTIONS = [
  { id: 'emergency3',  label: 'Emergency Meeting',   desc: '8 Free Spins × 3x',  costMult: 22.6,  color: '#FF5722', icon: 'emergency' },
  { id: 'emergency5',  label: 'Emergency Meeting+',  desc: '12 Free Spins × 5x', costMult: 56.6,  color: '#FF3D00', icon: 'emergency' },
  { id: 'emergency10', label: 'Emergency Meeting++', desc: '20 Free Spins × 10x', costMult: 188.6, color: '#D50000', icon: 'emergency' },
  { id: 'ventPick',    label: 'Vent Escape',         desc: 'Pick-a-Vent Bonus',  costMult: 16.3,  color: '#795548', icon: 'vent' },
  { id: 'taskWheel',   label: 'Task Wheel',          desc: 'Spin the Wheel',     costMult: 14.95, color: '#26A69A', icon: 'task' },
];

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
function SlotsPage({ user, userData, onUserDataChange, onTokenAdjustmentChange, onBack, isMobile }) {
  const toast = useToast();

  // Game state
  const [grid, setGrid] = useState(() => {
    const g = [];
    for (let r = 0; r < NUM_REELS; r++) {
      const col = [];
      for (let row = 0; row < NUM_ROWS; row++) {
        col.push(SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)].id);
      }
      g.push(col);
    }
    return g;
  });
  const [spinning, setSpinning] = useState(false);
  const [reelsStopped, setReelsStopped] = useState([true, true, true, true, true]);
  const [wins, setWins] = useState([]);
  const [totalWin, setTotalWin] = useState(0);
  const [showWin, setShowWin] = useState(false);
  const [highlightedLine, setHighlightedLine] = useState(-1);
  const [winCycleIndex, setWinCycleIndex] = useState(0);

  // Bet state
  const [betAmount, setBetAmount] = useState(1);
  const [tokenType, setTokenType] = useState('crewmate');
  const [autoSpin, setAutoSpin] = useState(false);
  const autoSpinRef = useRef(false);

  // Bonus state
  const [bonusMode, setBonusMode] = useState(null); // 'freeSpins', 'ventPick', 'taskWheel'
  const [bonusData, setBonusData] = useState(null);
  const [freeSpinIndex, setFreeSpinIndex] = useState(0);
  const [freeSpinTotal, setFreeSpinTotal] = useState(0);
  const [ventActive, setVentActive] = useState(false); // whether vent bonus is in progress
  const [ventRevealed, setVentRevealed] = useState(new Set()); // revealed cell keys "row-col"
  const [ventRevealedCells, setVentRevealedCells] = useState({}); // {"row-col": symbolId} from server
  const [ventRevealedSymbols, setVentRevealedSymbols] = useState({}); // {symbolId: count}
  const [ventMatched, setVentMatched] = useState(null); // matched symbol ID when 3 found
  const [ventPrize, setVentPrize] = useState(0);
  // ventRevealing removed — optimistic UI allows concurrent reveals
  const [wheelAngle, setWheelAngle] = useState(0);
  const [wheelSpinning, setWheelSpinning] = useState(false);
  const [wheelResult, setWheelResult] = useState(null);
  const [freeSpinsStarted, setFreeSpinsStarted] = useState(false); // true after user clicks "Start Bonus"
  const [freeSpinsComplete, setFreeSpinsComplete] = useState(false); // true when all spins done, shows summary

  // UI state
  const [showPaytable, setShowPaytable] = useState(false);
  const [showPaylines, setShowPaylines] = useState(false);
  const [showBonusBuy, setShowBonusBuy] = useState(false);
  const [buyingBonus, setBuyingBonus] = useState(false);
  const [muted, setMuted] = useState(false);
  const [history, setHistory] = useState([]);

  // Scatter highlight
  const [scatterPositions, setScatterPositions] = useState({});

  // Bonus landing / teaser state
  const [landedBonusCells, setLandedBonusCells] = useState(new Set()); // "reel-row" keys
  const [teaserActive, setTeaserActive] = useState(false);

  // Reel animation refs
  const reelAnimFrames = useRef([]);

  // Stable ref for handleSpin so bonus completions can resume auto-spin
  const handleSpinRef = useRef(null);

  // Token adjustment for optimistic UI
  const tokenAdjRef = useRef({ crewmate: 0, imposter: 0 });

  const balance = tokenType === 'crewmate'
    ? (userData?.crewmateTokens || 0) + tokenAdjRef.current.crewmate
    : (userData?.imposterTokens || 0) + tokenAdjRef.current.imposter;

  // Stable refs — httpsCallable returns a new reference each call, so
  // create once and reuse to avoid defeating useCallback memoisation.
  const spinSlots = useRef(httpsCallable(functions, 'spinSlots')).current;
  const revealVentCellFn = useRef(httpsCallable(functions, 'revealVentCell')).current;
  const buyBonusFn = useRef(httpsCallable(functions, 'buyBonus')).current;

  // ─── Win line cycling animation ───
  useEffect(() => {
    if (!showWin || wins.length === 0) return;
    const iv = setInterval(() => {
      setWinCycleIndex(prev => {
        const next = (prev + 1) % wins.length;
        setHighlightedLine(wins[next].lineIndex);
        return next;
      });
    }, 1200);
    return () => clearInterval(iv);
  }, [showWin, wins]);

  // ─── Auto-spin ───
  useEffect(() => {
    autoSpinRef.current = autoSpin;
    // When auto-spin stops and no spin is in flight, flush accumulated
    // balance adjustments.  If a spin IS in flight, its completion handler
    // will take the single-spin path and clean up instead.
    if (!autoSpin && !spinning) {
      if (tokenAdjRef.current.crewmate !== 0 || tokenAdjRef.current.imposter !== 0) {
        if (onTokenAdjustmentChange) {
          onTokenAdjustmentChange({
            crewmate: -tokenAdjRef.current.crewmate,
            imposter: -tokenAdjRef.current.imposter,
            frozen: false,
          });
        }
        (onUserDataChange ? Promise.resolve(onUserDataChange()) : Promise.resolve())
          .finally(() => {
            tokenAdjRef.current = { crewmate: 0, imposter: 0 };
            if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
          });
      }
    }
  }, [autoSpin, spinning, onTokenAdjustmentChange, onUserDataChange]);

  // ─── Reel spinning animation (pure visual — results come from server) ───
  // fast=true shortens animation for free spins / bonus rounds
  const animateReels = useCallback((serverGrid, fast = false) => {
    return new Promise((resolve) => {
      setReelsStopped([false, false, false, false, false]);
      setLandedBonusCells(new Set());
      setTeaserActive(false);
      audio.spinStart();

      const allSymIds = SYMBOLS.map(s => s.id);

      // Build a sequential symbol strip per reel for realistic scrolling
      const strips = [];
      for (let r = 0; r < NUM_REELS; r++) {
        const strip = [];
        // 60 symbols in the strip — shuffled but sequential for scrolling feel
        for (let i = 0; i < 60; i++) {
          strip.push(allSymIds[i % allSymIds.length]);
        }
        // Shuffle the strip
        for (let i = strip.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [strip[i], strip[j]] = [strip[j], strip[i]];
        }
        // Place the server result at the end so the strip "lands" on it
        strip[strip.length - 3] = serverGrid[r][0];
        strip[strip.length - 2] = serverGrid[r][1];
        strip[strip.length - 1] = serverGrid[r][2];
        strips.push(strip);
      }

      // Pre-scan server result for scatter positions to detect teaser
      const scattersByType = {};
      const scatterReels = {}; // { type: [reelIndex, ...] }
      for (let r = 0; r < NUM_REELS; r++) {
        for (let row = 0; row < NUM_ROWS; row++) {
          const sym = serverGrid[r][row];
          if (SCATTER_SYMBOLS.has(sym)) {
            if (!scattersByType[sym]) scattersByType[sym] = [];
            scattersByType[sym].push({ reel: r, row });
            if (!scatterReels[sym]) scatterReels[sym] = [];
            if (!scatterReels[sym].includes(r)) scatterReels[sym].push(r);
          }
        }
      }

      // Find teaser reel: the reel where 2nd scatter of any type lands
      // Only trigger teaser if there are remaining reels after it (no point on last reel)
      let teaserTriggerReel = -1;
      for (const [, positions] of Object.entries(scattersByType)) {
        if (positions.length >= 2) {
          const sorted = [...positions].sort((a, b) => a.reel - b.reel);
          const triggerReel = sorted[1].reel;
          // Must have at least one reel still spinning after this one
          if (triggerReel < NUM_REELS - 1) {
            if (teaserTriggerReel === -1 || triggerReel < teaserTriggerReel) {
              teaserTriggerReel = triggerReel;
            }
          }
        }
      }

      // Base durations — staggered per reel
      const baseDurations = fast
        ? [500, 700, 900, 1100, 1300]
        : [600, 850, 1050, 1250, 1450];

      // If teaser detected, extend reels after the trigger reel
      const reelDurations = [...baseDurations];
      if (teaserTriggerReel >= 0 && !fast) {
        for (let r = teaserTriggerReel + 1; r < NUM_REELS; r++) {
          reelDurations[r] = baseDurations[r] + 500 + (r - teaserTriggerReel) * 200;
        }
      }

      const timeoutIds = [];
      // Shared mutable state across reel callbacks (avoids closure warnings)
      const shared = { stopped: 0, teaserFired: false, accumulatedScatters: {} };

      const startReel = (r) => {
        const isAfterTeaser = teaserTriggerReel >= 0 && r > teaserTriggerReel && !fast;
        const baseTickInterval = fast ? 40 : 50;
        let elapsed = 0;
        let stripPos = 0;

        const tickReel = () => {
          elapsed += baseTickInterval;
          const progress = elapsed / reelDurations[r];

          // Dynamic tick interval — teaser reels slow down for suspense
          let currentTickInterval = baseTickInterval;
          if (isAfterTeaser && progress > 0.5) {
            // Gradual slowdown: 50ms → ~100ms max (keeps spinning looking smooth)
            currentTickInterval = baseTickInterval + Math.floor((progress - 0.5) * 100);
            audio.teaserTick();
          } else {
            audio.tick();
          }

          // Cycle through the strip sequentially
          stripPos++;
          const stripLen = strips[r].length;
          const nearEnd = progress > 0.85;
          setGrid(prev => {
            const next = prev.map(col => [...col]);
            if (nearEnd) {
              const endOffset = Math.max(0, stripLen - 3 - Math.floor((1 - progress) * 10));
              for (let row = 0; row < NUM_ROWS; row++) {
                next[r][row] = strips[r][Math.min(endOffset + row, stripLen - 1)];
              }
            } else {
              for (let row = 0; row < NUM_ROWS; row++) {
                next[r][row] = strips[r][(stripPos + row) % (stripLen - 3)];
              }
            }
            return next;
          });

          if (elapsed >= reelDurations[r]) {
            // Snap to server result
            setGrid(prev => {
              const next = prev.map(col => [...col]);
              for (let row = 0; row < NUM_ROWS; row++) {
                next[r][row] = serverGrid[r][row];
              }
              return next;
            });
            setReelsStopped(prev => {
              const next = [...prev];
              next[r] = true;
              return next;
            });
            audio.reelStop();

            // Check for bonus symbols in this reel and highlight immediately
            const newBonusCells = new Set();
            for (let row = 0; row < NUM_ROWS; row++) {
              if (SCATTER_SYMBOLS.has(serverGrid[r][row])) {
                newBonusCells.add(`${r}-${row}`);
                audio.bonusLand();

                const sym = serverGrid[r][row];
                if (!shared.accumulatedScatters[sym]) shared.accumulatedScatters[sym] = 0;
                shared.accumulatedScatters[sym]++;
              }
            }
            if (newBonusCells.size > 0) {
              setLandedBonusCells(prev => {
                const updated = new Set(prev);
                newBonusCells.forEach(k => updated.add(k));
                return updated;
              });
            }

            // Fire teaser when 2nd scatter of any type lands — only if reels remain
            if (!shared.teaserFired && !fast && shared.stopped + 1 < NUM_REELS) {
              for (const count of Object.values(shared.accumulatedScatters)) {
                if (count >= 2) {
                  shared.teaserFired = true;
                  setTeaserActive(true);
                  audio.bonusTeaser();
                  break;
                }
              }
            }

            shared.stopped++;
            if (shared.stopped === NUM_REELS) {
              setTeaserActive(false);
              resolve();
            }
          } else {
            // Schedule next tick with dynamic interval
            const tid = setTimeout(tickReel, currentTickInterval);
            timeoutIds.push(tid);
          }
        };

        // Start this reel's animation
        const tid = setTimeout(tickReel, baseTickInterval);
        timeoutIds.push(tid);
      };

      for (let r = 0; r < NUM_REELS; r++) {
        startReel(r);
      }

      reelAnimFrames.current = timeoutIds;
    });
  }, []);

  // ─── MAIN SPIN HANDLER ───
  const handleSpin = useCallback(async () => {
    if (spinning || bonusMode || bonusData || buyingBonus) return;

    let bet = parseFloat(betAmount);
    if (!Number.isFinite(bet) || bet < 0.1) {
      bet = 0.1;
      setBetAmount(0.1);
    }
    if (bet > 10000) {
      bet = 10000;
      setBetAmount(10000);
    }

    // Use adjusted balance (includes optimistic deductions from prior auto-spin
    // rounds) so we stop early instead of sending doomed requests to the server.
    const currentBalance = tokenType === 'crewmate'
      ? (userData?.crewmateTokens || 0) + tokenAdjRef.current.crewmate
      : (userData?.imposterTokens || 0) + tokenAdjRef.current.imposter;

    if (bet > currentBalance) {
      toast.error('Insufficient tokens');
      setAutoSpin(false);
      return;
    }

    audio.init();
    setSpinning(true);
    setShowWin(false);
    setWins([]);
    setTotalWin(0);
    setHighlightedLine(-1);
    setScatterPositions({});
    setLandedBonusCells(new Set());
    setTeaserActive(false);

    // Immediately deduct bet from displayed balance and freeze to prevent
    // the 10-second poll from updating userData mid-spin
    if (tokenType === 'crewmate') {
      tokenAdjRef.current.crewmate -= bet;
    } else {
      tokenAdjRef.current.imposter -= bet;
    }
    if (onTokenAdjustmentChange) {
      onTokenAdjustmentChange({
        crewmate: -tokenAdjRef.current.crewmate,
        imposter: -tokenAdjRef.current.imposter,
        frozen: true,
      });
    }

    try {
      const result = await spinSlots({ betAmount: bet, tokenType });
      const data = result.data;

      // Animate reels to server result
      await animateReels(data.grid);

      // Show scatter highlights
      if (data.scatterPositions) {
        setScatterPositions(data.scatterPositions);
      }

      // When a bonus triggers, only show line wins immediately — the bonus
      // payout is revealed during the bonus animation, not the base spin.
      const displayWin = data.bonusData ? (data.lineWin || 0) : (data.totalWin || 0);

      // Show wins
      if (data.wins && data.wins.length > 0) {
        setWins(data.wins);
        setHighlightedLine(data.wins[0].lineIndex);
        setShowWin(true);
        const winMultiple = displayWin / bet;
        if (winMultiple >= 100) {
          audio.epicWin();
        } else if (winMultiple >= 50) {
          audio.megaWin();
        } else if (winMultiple >= 25) {
          audio.bigWin();
        } else if (winMultiple >= 10) {
          audio.jackpot();
        } else {
          audio.win(winMultiple >= 3);
        }
      } else if (!data.bonusData) {
        audio.lose();
      }

      setTotalWin(displayWin);

      // Add to history — use full totalWin (including bonus) for the record
      setHistory(prev => [{ grid: data.grid, win: data.totalWin || 0, bet, time: Date.now() }, ...prev.slice(0, 29)]);

      // Handle bonuses
      if (data.bonusData) {
        setBonusData(data.bonusData);

        if (data.bonusData.type === 'freeSpins') {
          audio.emergencyAlarm();
          setTimeout(() => {
            setBonusMode('freeSpins');
            setFreeSpinIndex(0);
            setFreeSpinTotal(0);
            setFreeSpinsStarted(false);
            setFreeSpinsComplete(false);
          }, 1500);
        } else if (data.bonusData.type === 'ventPick') {
          audio.ventSound();
          setTimeout(() => {
            setBonusMode('ventPick');
            setVentActive(true);
            setVentRevealed(new Set());
            setVentRevealedCells({});
            setVentRevealedSymbols({});
            setVentMatched(null);
            setVentPrize(data.bonusData.prize);
          }, 1500);
        } else if (data.bonusData.type === 'taskWheel') {
          audio.taskChime();
          setTimeout(() => {
            setBonusMode('taskWheel');
            setWheelAngle(0);
            setWheelSpinning(false);
            setWheelResult(null);
          }, 1500);
        }
      }

      setSpinning(false);

      // Update balance display with server-confirmed result.
      // Bet was already deducted optimistically before the spin, so only
      // add back the winnings here.  When a bonus is triggered, only show
      // the line win immediately — the bonus payout is deferred until the
      // bonus animation finishes so the balance doesn't spoil the reveal.
      const immediateWin = data.bonusData ? (data.lineWin || 0) : (data.totalWin || 0);
      if (immediateWin > 0) {
        if (tokenType === 'crewmate') {
          tokenAdjRef.current.crewmate += immediateWin;
        } else {
          tokenAdjRef.current.imposter += immediateWin;
        }
      }

      if (autoSpinRef.current && !data.bonusData) {
        // During auto-spin: keep adjustments accumulated so the
        // Nav and SlotsPage balance update live without waiting for a
        // Firestore re-read (which can return stale cached data).
        // Nav subtracts tokenAdjustment, so invert sign for correct display
        if (onTokenAdjustmentChange) {
          onTokenAdjustmentChange({
            crewmate: -tokenAdjRef.current.crewmate,
            imposter: -tokenAdjRef.current.imposter,
            frozen: true,
          });
        }
        // Continue auto-spin
        setTimeout(() => {
          if (autoSpinRef.current) handleSpinRef.current();
        }, 1500);
      } else if (data.bonusData) {
        // Bonus triggered: keep balance frozen showing only bet + lineWin
        // adjustments. The bonus completion handler will refresh when done.
      } else {
        // Single spin, no bonus: push current adjustment (unfrozen) so the
        // Nav shows the correct post-spin balance immediately, then refresh
        // from Firestore.  Only clear the adjustment AFTER Firestore returns
        // to avoid a flash where Nav shows stale userData with zero adjustment.
        if (onTokenAdjustmentChange) {
          onTokenAdjustmentChange({
            crewmate: -tokenAdjRef.current.crewmate,
            imposter: -tokenAdjRef.current.imposter,
            frozen: false,
          });
        }
        (onUserDataChange ? Promise.resolve(onUserDataChange()) : Promise.resolve())
          .finally(() => {
            tokenAdjRef.current = { crewmate: 0, imposter: 0 };
            if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
          });
      }

    } catch (err) {
      setSpinning(false);
      // Unfreeze display and refresh data on error
      tokenAdjRef.current = { crewmate: 0, imposter: 0 };
      if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
      if (onUserDataChange) onUserDataChange();
      const msg = err?.message || 'Spin failed';
      const isInsufficientFunds = msg.includes('Insufficient') || msg.includes('insufficient');
      toast.error(isInsufficientFunds ? 'Insufficient tokens' : msg);
      // Always stop auto-spin on error to prevent error loops
      setAutoSpin(false);
    }
  }, [spinning, bonusMode, bonusData, buyingBonus, betAmount, tokenType, userData, onTokenAdjustmentChange, onUserDataChange, animateReels, toast, spinSlots]);

  // Keep handleSpinRef current so bonus callbacks can invoke it without stale closures
  useEffect(() => { handleSpinRef.current = handleSpin; }, [handleSpin]);

  // ─── Free spin animation (plays bonus spins sequentially) ───
  // Use a ref to avoid stale closure issues — bonusData may not be latest in the callback
  const bonusDataRef = useRef(null);
  useEffect(() => { bonusDataRef.current = bonusData; }, [bonusData]);

  const playFreeSpins = useCallback(async () => {
    const bd = bonusDataRef.current;
    if (!bd || bd.type !== 'freeSpins' || !bd.spinResults) return;
    const spins = bd.spinResults;
    let total = 0;

    for (let i = 0; i < spins.length; i++) {
      setFreeSpinIndex(i + 1);
      await animateReels(spins[i].grid, true); // fast mode for bonus spins

      if (spins[i].wins && spins[i].wins.length > 0) {
        setWins(spins[i].wins);
        setHighlightedLine(spins[i].wins[0].lineIndex);
        setShowWin(true);
        if (spins[i].spinWin > 0) {
          // Estimate multiplier relative to base bet for sound tier
          const spinMult = spins[i].spinWin / (bd.multiplier || 1);
          if (spinMult >= 50) audio.megaWin();
          else if (spinMult >= 20) audio.bigWin();
          else audio.win(spinMult >= 5);
        }
      }

      total += spins[i].spinWin;
      setFreeSpinTotal(total);

      // Add each free spin result to history
      setHistory(prev => [{ grid: spins[i].grid, win: spins[i].spinWin, bet: 0, bonus: 'Free Spin', time: Date.now() }, ...prev.slice(0, 29)]);

      if (spins[i].retrigger) {
        audio.emergencyAlarm();
        await new Promise(r => setTimeout(r, 600));
      }

      // Brief pause between spins (shorter for non-winning spins)
      const pauseMs = spins[i].spinWin > 0 ? 800 : 400;
      await new Promise(r => setTimeout(r, pauseMs));
      setShowWin(false);
      setWins([]);
      setHighlightedLine(-1);
      setLandedBonusCells(new Set());
    }

    setFreeSpinTotal(total);
    setHighlightedLine(-1);
    setLandedBonusCells(new Set());
    setFreeSpinsComplete(true);
  }, [animateReels]);

  useEffect(() => {
    if (bonusMode === 'freeSpins' && bonusData && freeSpinsStarted) {
      playFreeSpins();
    }
  }, [bonusMode, freeSpinsStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Vent match-3 grid handler (server-side reveals, optimistic UI) ───
  const ventPendingRef = useRef(new Set()); // track in-flight cell keys
  const ventQueueRef = useRef([]); // queue of {row, col} for serialized reveals
  const ventProcessingRef = useRef(false); // true while processing queue
  // Process queued vent reveals one at a time
  const processVentQueue = useCallback(async () => {
    if (ventProcessingRef.current) return;
    ventProcessingRef.current = true;

    while (ventQueueRef.current.length > 0) {
      const { row, col } = ventQueueRef.current.shift();
      const key = `${row}-${col}`;

      try {
        const result = await revealVentCellFn({ row, col });
        const { symbolId, multiplier: cellMultiplier, matched, won, prize, alreadyRevealed } = result.data;

        ventPendingRef.current.delete(key);

        if (alreadyRevealed) continue;

        // Update with real server data — store multiplier for display
        setVentRevealedCells(prev => ({ ...prev, [key]: { symbolId, multiplier: cellMultiplier || 0 } }));

        if (symbolId >= 0) {
          setVentRevealedSymbols(prev => ({ ...prev, [symbolId]: (prev[symbolId] || 0) + 1 }));
        }

        if (matched) {
          // Clear remaining queue — bonus is done
          ventQueueRef.current = [];

          setVentMatched(symbolId);

          if (won && prize > 0) {
            const ventBet = parseFloat(betAmount) || 1;
            const ventMult = prize / ventBet;
            if (ventMult >= 100) audio.epicWin();
            else if (ventMult >= 50) audio.megaWin();
            else if (ventMult >= 25) audio.bigWin();
            else audio.win(true);

            setVentPrize(prize);
            setHistory(prev => [{ grid: null, win: prize, bet: 0, bonus: 'Vent Match-3', time: Date.now() }, ...prev.slice(0, 29)]);
            toast.success(`Vent Bonus! Matched 3 — Won ${fmtTokens(prize)} tokens!`);
          } else {
            setVentPrize(0);
            audio.lose();
            toast.error('Wrong match! Bonus forfeited.');
          }

          setTimeout(() => {
            setBonusMode(null);
            setBonusData(null);
            setVentActive(false);
            setShowWin(false);
            setWins([]);
            setTotalWin(0);
            (onUserDataChange ? Promise.resolve(onUserDataChange()) : Promise.resolve())
              .finally(() => {
                tokenAdjRef.current = { crewmate: 0, imposter: 0 };
                if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
                if (autoSpinRef.current) {
                  setTimeout(() => handleSpinRef.current(), 500);
                }
              });
          }, 2500);
          break;
        }
      } catch (err) {
        ventPendingRef.current.delete(key);
        // Revert optimistic reveal on error
        setVentRevealed(prev => { const n = new Set(prev); n.delete(key); return n; });
        setVentRevealedCells(prev => { const n = { ...prev }; delete n[key]; return n; });
        const msg = err?.message || 'Failed to reveal cell';
        if (msg.includes('No pending')) {
          // Bonus expired — clear queue, unfreeze balance, and end
          ventQueueRef.current = [];
          toast.error('Bonus expired');
          setBonusMode(null);
          setBonusData(null);
          setVentActive(false);
          setShowWin(false);
          setWins([]);
          setTotalWin(0);
          (onUserDataChange ? Promise.resolve(onUserDataChange()) : Promise.resolve())
            .finally(() => {
              tokenAdjRef.current = { crewmate: 0, imposter: 0 };
              if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
            });
          break;
        }
        // Rate limit or transient error — just revert this cell, don't kill the bonus
        // Player can click again
      }
    }

    ventProcessingRef.current = false;
  }, [betAmount, revealVentCellFn, toast, onUserDataChange, onTokenAdjustmentChange]);

  const handleVentReveal = useCallback(async (row, col) => {
    if (!ventActive || ventMatched !== null) return;
    const key = `${row}-${col}`;
    if (ventRevealed.has(key) || ventPendingRef.current.has(key)) return;

    // Optimistic: immediately mark cell as revealed with a pending state
    ventPendingRef.current.add(key);
    setVentRevealed(prev => { const n = new Set(prev); n.add(key); return n; });
    setVentRevealedCells(prev => ({ ...prev, [key]: 'pending' }));
    audio.ventSound();

    // Queue the server call instead of firing immediately
    ventQueueRef.current.push({ row, col });
    processVentQueue();
  }, [ventActive, ventRevealed, ventMatched, processVentQueue]);

  // ─── Task wheel spin ───
  const handleWheelSpin = useCallback(() => {
    if (!bonusData || wheelSpinning) return;
    setWheelSpinning(true);
    audio.init();

    const segments = bonusData.segments.length;
    const segmentAngle = 360 / segments;
    const targetAngle = 360 * 5 + (360 - bonusData.winIndex * segmentAngle - segmentAngle / 2);

    let startTime = null;
    const duration = 4000;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const currentAngle = eased * targetAngle;
      setWheelAngle(currentAngle);

      // Tick sound at segment boundaries
      if (progress < 0.95) {
        const segsCrossed = Math.floor(currentAngle / segmentAngle);
        if (segsCrossed !== Math.floor((currentAngle - (targetAngle / duration) * 16) / segmentAngle)) {
          audio.wheelTick();
        }
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setWheelSpinning(false);
        setWheelResult(bonusData);
        // Tiered bonus win sound
        const wheelMult = bonusData.segments[bonusData.winIndex]?.multiplier || 0;
        if (wheelMult >= 1000) audio.epicWin();
        else if (wheelMult >= 250) audio.megaWin();
        else if (wheelMult >= 75) audio.bigWin();
        else audio.win(true);
        // Add task wheel bonus to history
        setHistory(prev => [{ grid: null, win: bonusData.prize, bet: 0, bonus: 'Task Wheel', time: Date.now() }, ...prev.slice(0, 29)]);
        toast.success(`Task Bonus! Won ${fmtTokens(bonusData.prize)} tokens!`);
        setTimeout(() => {
          setBonusMode(null);
          setBonusData(null);
          setShowWin(false);
          setWins([]);
          setTotalWin(0);
          (onUserDataChange ? Promise.resolve(onUserDataChange()) : Promise.resolve())
            .finally(() => {
              tokenAdjRef.current = { crewmate: 0, imposter: 0 };
              if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
              // Resume auto-spin after bonus
              if (autoSpinRef.current) {
                setTimeout(() => handleSpinRef.current(), 500);
              }
            });
        }, 2500);
      }
    };
    requestAnimationFrame(animate);
  }, [bonusData, wheelSpinning, toast, onUserDataChange, onTokenAdjustmentChange]);

  // ─── Bonus Buy Handler ───

  const handleBuyBonus = useCallback(async (bonusId) => {
    if (spinning || bonusMode || buyingBonus) return;

    const option = BONUS_BUY_OPTIONS.find(o => o.id === bonusId);
    if (!option) return;

    const bet = parseFloat(betAmount) || 0.1;
    const cost = bet * option.costMult;

    if (cost > balance) {
      toast.error(`Insufficient tokens. Need ${fmtTokens(cost)}`);
      return;
    }

    audio.init();
    setBuyingBonus(true);
    setShowBonusBuy(false);

    // Clear any lingering visual state from previous spins
    setShowWin(false);
    setWins([]);
    setHighlightedLine(-1);
    setScatterPositions({});
    setLandedBonusCells(new Set());
    setTeaserActive(false);

    // Immediately freeze balance display and apply optimistic deduction so
    // the 10-second auto-refresh can't leak the server-side winnings before
    // the bonus animation has played out.
    if (tokenType === 'crewmate') {
      tokenAdjRef.current.crewmate -= cost;
    } else {
      tokenAdjRef.current.imposter -= cost;
    }
    if (onTokenAdjustmentChange) {
      onTokenAdjustmentChange({
        crewmate: -tokenAdjRef.current.crewmate,
        imposter: -tokenAdjRef.current.imposter,
        frozen: true,
      });
    }

    try {
      const result = await buyBonusFn({ bonusId, betAmount: bet, tokenType });
      const data = result.data;

      if (data.bonusData) {
        setBonusData(data.bonusData);

        if (data.bonusData.type === 'freeSpins') {
          audio.emergencyAlarm();
          setTimeout(() => {
            setBonusMode('freeSpins');
            setFreeSpinIndex(0);
            setFreeSpinTotal(0);
            setFreeSpinsStarted(false);
            setFreeSpinsComplete(false);
            setBuyingBonus(false);
          }, 800);
        } else if (data.bonusData.type === 'ventPick') {
          audio.ventSound();
          setTimeout(() => {
            setBonusMode('ventPick');
            setVentActive(true);
            setVentRevealed(new Set());
            setVentRevealedCells({});
            setVentRevealedSymbols({});
            setVentMatched(null);
            setVentPrize(data.bonusData.prize);
            setBuyingBonus(false);
          }, 800);
        } else if (data.bonusData.type === 'taskWheel') {
          audio.taskChime();
          setTimeout(() => {
            setBonusMode('taskWheel');
            setWheelAngle(0);
            setWheelSpinning(false);
            setWheelResult(null);
            setBuyingBonus(false);
          }, 800);
        }
      }

      // Don't refresh balance here — bonus animation hasn't played yet.
      // Each bonus completion handler calls onUserDataChange() when done.
    } catch (err) {
      // Revert optimistic balance deduction and refresh from server
      tokenAdjRef.current = { crewmate: 0, imposter: 0 };
      if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
      if (onUserDataChange) onUserDataChange();
      setBuyingBonus(false);
      const msg = err?.message || 'Bonus buy failed';
      toast.error(msg.includes('Insufficient') ? `Insufficient tokens` : msg);
    }
  }, [spinning, bonusMode, buyingBonus, betAmount, balance, tokenType, toast, buyBonusFn, onUserDataChange, onTokenAdjustmentChange]);

  // ─── Keyboard: space to spin ───
  useEffect(() => {
    const handler = (e) => {
      if (e.code === 'Space' && !spinning && !bonusMode && !bonusData && !showPaytable && !showBonusBuy && !buyingBonus) {
        e.preventDefault();
        handleSpin();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [spinning, bonusMode, bonusData, showPaytable, showBonusBuy, buyingBonus, handleSpin]);

  // ─── Mute toggle sync ───
  useEffect(() => { audio.muted = muted; }, [muted]);

  // ─── Stop auto-spin & mute when user tabs away ───
  useEffect(() => {
    const handleVisibility = () => {
      if (document.hidden) {
        if (autoSpinRef.current) {
          setAutoSpin(false);
        }
        audio.muted = true;
      } else {
        // Restore mute state to user's preference when returning
        audio.muted = muted;
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [muted]);

  // ─── Cleanup ───
  useEffect(() => {
    return () => {
      reelAnimFrames.current.forEach(id => clearTimeout(id));
    };
  }, []);

  // ─── STYLES ───
  const S = {
    page: {
      position: isMobile ? 'relative' : 'fixed',
      top: isMobile ? 0 : 0,
      left: isMobile ? undefined : 'var(--sidebar-w)',
      right: isMobile ? undefined : 0,
      bottom: isMobile ? undefined : 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      background: 'linear-gradient(180deg, #0a0a1a 0%, #0d0518 40%, #050508 100%)',
      overflow: 'auto', padding: isMobile ? '12px' : '16px',
      minHeight: isMobile ? 'calc(100vh - 48px - 60px)' : undefined,
      paddingBottom: isMobile ? '80px' : '16px',
    },
    header: {
      display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '16px', width: '100%', maxWidth: '900px', marginBottom: '12px',
    },
    backBtn: {
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: '12px', padding: isMobile ? '8px 12px' : '8px 16px', color: '#aaa', cursor: 'pointer',
      fontSize: isMobile ? '12px' : '13px', fontWeight: '600',
    },
    title: {
      fontSize: isMobile ? '16px' : '22px', fontWeight: '800', letterSpacing: isMobile ? '1px' : '3px', textTransform: 'uppercase',
      background: 'linear-gradient(135deg, #9B59B6, #E91E63, #FF5722)',
      WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
    },
    machine: {
      background: 'linear-gradient(180deg, #1a1028 0%, #12091e 50%, #0a0514 100%)',
      border: '2px solid rgba(155,89,182,0.4)',
      borderRadius: isMobile ? '16px' : '24px', padding: isMobile ? '10px' : '20px', maxWidth: '720px', width: '100%',
      boxShadow: '0 0 60px rgba(155,89,182,0.15), inset 0 0 30px rgba(0,0,0,0.5)',
      position: 'relative',
    },
    reelsContainer: {
      display: 'flex', gap: isMobile ? '2px' : '4px', justifyContent: 'center',
      background: 'rgba(0,0,0,0.6)', borderRadius: isMobile ? '10px' : '16px', padding: isMobile ? '6px 4px' : '12px 8px',
      border: '1px solid rgba(155,89,182,0.2)', position: 'relative',
      overflow: 'hidden',
    },
    reel: (stopped) => ({
      display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center',
      flex: '1 1 0', minWidth: 0,
      transition: stopped ? 'transform 0.1s ease' : 'none',
    }),
    cell: (isWinning, isScatter, isBonusLanded, isTeaserPulse) => ({
      width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? '20px' : undefined,
      background: isWinning ? 'rgba(255,215,0,0.15)'
        : isBonusLanded ? 'rgba(255,87,34,0.2)'
        : isScatter ? 'rgba(255,87,34,0.12)'
        : 'rgba(20,10,30,0.8)',
      borderRadius: '12px',
      border: isWinning ? '2px solid rgba(255,215,0,0.6)'
        : isBonusLanded ? '2px solid rgba(255,152,0,0.8)'
        : isScatter ? '2px solid rgba(255,87,34,0.4)'
        : '1px solid rgba(255,255,255,0.05)',
      boxShadow: isWinning ? '0 0 20px rgba(255,215,0,0.3), inset 0 0 10px rgba(255,215,0,0.1)'
        : isBonusLanded ? '0 0 20px rgba(255,152,0,0.5), 0 0 40px rgba(255,87,34,0.25), inset 0 0 10px rgba(255,152,0,0.15)'
        : 'none',
      transition: 'all 0.3s ease',
      position: 'relative',
      overflow: 'hidden',
      animation: isTeaserPulse && isBonusLanded ? 'teaserPulse 0.6s ease-in-out infinite' : isBonusLanded ? 'bonusLand 0.4s ease-out' : 'none',
    }),
    controls: {
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? '8px' : '12px',
      marginTop: isMobile ? '10px' : '16px', flexWrap: 'wrap',
    },
    spinBtn: (disabled) => ({
      background: disabled
        ? 'rgba(100,100,100,0.3)'
        : 'linear-gradient(135deg, #9B59B6, #E91E63)',
      border: 'none', borderRadius: '50%', width: isMobile ? '56px' : '72px', height: isMobile ? '56px' : '72px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      cursor: disabled ? 'not-allowed' : 'pointer', color: '#fff',
      fontSize: isMobile ? '11px' : '14px', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase',
      boxShadow: disabled ? 'none' : '0 0 30px rgba(155,89,182,0.4), 0 4px 15px rgba(0,0,0,0.3)',
      transition: 'all 0.2s ease',
      fontFamily: 'Orbitron, monospace',
    }),
    betInput: {
      background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(155,89,182,0.3)',
      borderRadius: '10px', padding: '8px 12px', color: '#fff', fontSize: isMobile ? '16px' : '14px',
      width: isMobile ? '80px' : '100px', textAlign: 'center', fontFamily: 'Orbitron, monospace',
    },
    tokenToggle: (active) => ({
      padding: '8px 16px', borderRadius: '10px', cursor: 'pointer',
      fontSize: '12px', fontWeight: '700', letterSpacing: '1px',
      background: active ? 'rgba(155,89,182,0.3)' : 'rgba(255,255,255,0.05)',
      border: active ? '1px solid rgba(155,89,182,0.6)' : '1px solid rgba(255,255,255,0.1)',
      color: active ? '#fff' : '#888', transition: 'all 0.2s ease',
    }),
    quickBet: {
      padding: '6px 12px', borderRadius: '8px', cursor: 'pointer',
      fontSize: '11px', fontWeight: '600', color: '#bbb',
      background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)',
      transition: 'all 0.2s ease',
    },
    winBanner: {
      textAlign: 'center', padding: '12px', marginTop: '12px',
      background: 'linear-gradient(135deg, rgba(255,215,0,0.15), rgba(255,152,0,0.1))',
      borderRadius: '16px', border: '1px solid rgba(255,215,0,0.3)',
    },
    balanceBar: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: isMobile ? '6px 10px' : '8px 16px', marginBottom: isMobile ? '8px' : '12px',
      background: 'rgba(0,0,0,0.3)', borderRadius: '12px',
      border: '1px solid rgba(255,255,255,0.05)',
      flexWrap: isMobile ? 'wrap' : undefined, gap: isMobile ? '4px' : undefined,
      fontSize: isMobile ? '12px' : undefined,
    },
  };

  // ─── Check if a cell is part of a winning line ───
  const isCellWinning = (reel, row) => {
    if (highlightedLine < 0) return false;
    const line = PAYLINES[highlightedLine];
    return line && line[reel] === row;
  };

  const isCellScatter = (reel, row) => {
    for (const positions of Object.values(scatterPositions)) {
      if (positions && positions.some(p => p.reel === reel && p.row === row)) return true;
    }
    return false;
  };

  // ─── PAYTABLE MODAL ───
  const renderPaytable = () => (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.85)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }} onClick={() => setShowPaytable(false)}>
      <div style={{
        background: 'linear-gradient(180deg, #1a1028, #0a0514)',
        border: '2px solid rgba(155,89,182,0.4)', borderRadius: '24px',
        padding: '28px', maxWidth: '700px', width: '100%', maxHeight: '80vh',
        overflow: 'auto',
      }} onClick={e => e.stopPropagation()}>
        <h3 style={{ textAlign: 'center', fontSize: '18px', fontWeight: '800', color: '#FFD700', marginBottom: '20px', letterSpacing: '2px' }}>
          PAYTABLE
        </h3>
        <div style={{ fontSize: '11px', color: '#888', textAlign: 'center', marginBottom: '16px' }}>
          Payouts shown are relative to your total bet. Multiple paylines can win on a single spin.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          {Object.entries(PAYTABLE).map(([sym, pays]) => (
            <div key={sym} style={{
              background: 'rgba(0,0,0,0.4)', borderRadius: '12px', padding: '12px',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex', alignItems: 'center', gap: '10px',
            }}>
              <div style={{ flexShrink: 0 }}>{renderSymbol(sym, 36)}</div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '700', color: '#ddd', marginBottom: '4px' }}>
                  {SYMBOLS.find(s => s.id === sym)?.name || sym}
                </div>
                {Object.entries(pays).sort((a, b) => b[0] - a[0]).map(([count, mult]) => (
                  <div key={count} style={{ fontSize: '11px', color: '#999' }}>
                    {count} on a line → <span style={{ color: '#FFD700' }}>pays {parseFloat((mult / 20).toFixed(2))}×</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <h4 style={{ color: '#FF5722', marginTop: '20px', marginBottom: '10px', fontSize: '14px', fontWeight: '700' }}>
          SCATTER BONUSES (appear anywhere on the grid)
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
          {[
            { sym: 'emergency', label: 'Emergency Meeting', lines: [
              '3 → 8 Free Spins (3× multiplier)',
              '4 → 12 Free Spins (5× multiplier)',
              '5 → 20 Free Spins (10× multiplier)',
            ], color: '#FF5722' },
            { sym: 'vent', label: 'Vent Escape', lines: [
              '3+ → Pick-a-Vent bonus game',
              'Match 3 symbols to win a prize',
              '4+ scatters = bigger prizes',
            ], color: '#795548' },
            { sym: 'task', label: 'Task Complete', lines: [
              '3+ → Spin the bonus wheel',
              'Win up to 5000× your bet',
              '4+ scatters = bigger prizes',
            ], color: '#26A69A' },
          ].map(b => (
            <div key={b.sym} style={{
              background: 'rgba(0,0,0,0.4)', borderRadius: '12px', padding: '12px',
              border: `1px solid ${b.color}40`, textAlign: 'center',
            }}>
              <div style={{ marginBottom: '6px' }}>{renderSymbol(b.sym, 36)}</div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: b.color, marginBottom: '4px' }}>{b.label}</div>
              {b.lines.map((line, i) => (
                <div key={i} style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>{line}</div>
              ))}
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <div style={{ fontSize: '10px', color: '#666' }}>20 Paylines • Wild substitutes for all regular symbols • Scatters don't need to be on a payline</div>
          <button onClick={() => setShowPaytable(false)} style={{
            marginTop: '12px', padding: '8px 24px', borderRadius: '10px',
            background: 'rgba(155,89,182,0.3)', border: '1px solid rgba(155,89,182,0.5)',
            color: '#fff', cursor: 'pointer', fontSize: '12px', fontWeight: '700',
          }}>Close</button>
        </div>
      </div>
    </div>
  );

  // ─── BONUS BUY MENU ───
  const renderBonusBuyMenu = () => {
    const bet = parseFloat(betAmount) || 0.1;
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.92)', zIndex: 1000,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }} onClick={() => setShowBonusBuy(false)}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(30,30,40,0.98), rgba(20,20,30,0.98))',
          border: '2px solid rgba(255,87,34,0.5)', borderRadius: '20px',
          padding: '28px 32px', maxWidth: '520px', width: '90%',
          boxShadow: '0 0 60px rgba(255,87,34,0.15), inset 0 0 30px rgba(255,87,34,0.05)',
        }} onClick={e => e.stopPropagation()}>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '24px', fontWeight: '900', color: '#FF5722', letterSpacing: '2px', textShadow: '0 0 20px rgba(255,87,34,0.5)' }}>
              BUY BONUS
            </div>
            <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
              Current bet: {fmtTokens(bet)} {tokenType === 'crewmate' ? '🔵' : '🔴'}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {BONUS_BUY_OPTIONS.map(opt => {
              const cost = bet * opt.costMult;
              const canAfford = cost <= balance;
              return (
                <button
                  key={opt.id}
                  onClick={() => canAfford && handleBuyBonus(opt.id)}
                  disabled={!canAfford || spinning || bonusMode || buyingBonus}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '14px',
                    padding: '14px 18px', borderRadius: '14px',
                    background: canAfford
                      ? `linear-gradient(135deg, ${opt.color}18, ${opt.color}08)`
                      : 'rgba(255,255,255,0.02)',
                    border: `1.5px solid ${canAfford ? opt.color + '60' : 'rgba(255,255,255,0.08)'}`,
                    cursor: canAfford ? 'pointer' : 'not-allowed',
                    opacity: canAfford ? 1 : 0.4,
                    transition: 'all 0.2s',
                    textAlign: 'left',
                  }}
                  onMouseEnter={e => { if (canAfford) { e.currentTarget.style.background = `linear-gradient(135deg, ${opt.color}30, ${opt.color}15)`; e.currentTarget.style.boxShadow = `0 0 20px ${opt.color}20`; } }}
                  onMouseLeave={e => { e.currentTarget.style.background = canAfford ? `linear-gradient(135deg, ${opt.color}18, ${opt.color}08)` : 'rgba(255,255,255,0.02)'; e.currentTarget.style.boxShadow = 'none'; }}
                >
                  <div style={{ flexShrink: 0, width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {renderSymbol(opt.icon, 40, true)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', fontWeight: '800', color: canAfford ? opt.color : '#666' }}>
                      {opt.label}
                    </div>
                    <div style={{ fontSize: '11px', color: canAfford ? '#aaa' : '#555', marginTop: '2px' }}>
                      {opt.desc}
                    </div>
                  </div>
                  <div style={{ flexShrink: 0, textAlign: 'right' }}>
                    <div style={{ fontSize: '16px', fontWeight: '900', color: canAfford ? '#FFD700' : '#555' }}>
                      {fmtTokens(cost)}
                    </div>
                    <div style={{ fontSize: '9px', color: '#666', letterSpacing: '0.5px' }}>
                      {opt.costMult}× BET
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <button onClick={() => setShowBonusBuy(false)} style={{
            marginTop: '16px', width: '100%', padding: '10px', borderRadius: '10px',
            background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
            color: '#888', cursor: 'pointer', fontSize: '12px', fontWeight: '700',
          }}>Close</button>
        </div>
      </div>
    );
  };

  // ─── FREE SPINS INTRO (full-screen, before user starts) ───
  const renderFreeSpinsIntro = () => (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.92)', zIndex: 1000,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'linear-gradient(135deg, rgba(255,87,34,0.2), rgba(255,23,68,0.15))',
        borderRadius: '24px', padding: '32px 48px', border: '2px solid rgba(255,87,34,0.5)',
        textAlign: 'center', maxWidth: '400px',
        boxShadow: '0 0 60px rgba(255,87,34,0.2), 0 0 120px rgba(255,23,68,0.1)',
      }}>
        <div style={{ marginBottom: '12px' }}>
          <EmergencyButtonSVG size={64} glow />
        </div>
        <div style={{ fontSize: '12px', fontWeight: '700', color: '#FF5722', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '4px' }}>
          EMERGENCY MEETING
        </div>
        <div style={{ fontSize: '28px', fontWeight: '900', color: '#FFD700', margin: '8px 0', textShadow: '0 0 20px rgba(255,215,0,0.5)' }}>
          FREE SPINS!
        </div>
        <div style={{ display: 'flex', gap: '24px', justifyContent: 'center', margin: '16px 0' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '900', color: '#fff' }}>{bonusData?.totalSpins || bonusData?.spins || 0}</div>
            <div style={{ fontSize: '10px', color: '#aaa', letterSpacing: '1px' }}>SPINS</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '900', color: '#FF9100' }}>{bonusData?.multiplier}×</div>
            <div style={{ fontSize: '10px', color: '#aaa', letterSpacing: '1px' }}>MULTIPLIER</div>
          </div>
        </div>
        <button
          onClick={() => setFreeSpinsStarted(true)}
          style={{
            marginTop: '16px', padding: '14px 40px', borderRadius: '14px',
            background: 'linear-gradient(135deg, #FF5722, #E91E63)',
            border: 'none', color: '#fff', fontSize: '16px', fontWeight: '800',
            cursor: 'pointer', letterSpacing: '2px', textTransform: 'uppercase',
            boxShadow: '0 0 30px rgba(255,87,34,0.4), 0 4px 15px rgba(0,0,0,0.3)',
            transition: 'transform 0.2s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          START BONUS
        </button>
      </div>
    </div>
  );

  // ─── FREE SPINS HUD (compact bar shown during active spins) ───
  const renderFreeSpinsHUD = () => (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px',
      padding: '8px 20px', marginBottom: '8px',
      background: 'linear-gradient(135deg, rgba(255,87,34,0.2), rgba(255,23,68,0.12))',
      borderRadius: '12px', border: '1px solid rgba(255,87,34,0.4)',
    }}>
      <span style={{ fontSize: '10px', fontWeight: '700', color: '#FF5722', letterSpacing: '2px' }}>EMERGENCY MEETING</span>
      <span style={{ fontSize: '14px', fontWeight: '900', color: '#FFD700' }}>
        Spin {freeSpinIndex} / {bonusData?.totalSpins || bonusData?.spins || 0}
      </span>
      <span style={{ fontSize: '12px', fontWeight: '700', color: '#FF9100' }}>{bonusData?.multiplier}× Mult</span>
      <span style={{ fontSize: '14px', fontWeight: '800', color: '#00E676' }}>Won: {fmtTokens(freeSpinTotal)}</span>
    </div>
  );

  // ─── FREE SPINS COMPLETION SUMMARY ───
  const dismissFreeSpins = useCallback(() => {
    setFreeSpinsComplete(false);
    setBonusMode(null);
    setBonusData(null);
    setShowWin(false);
    setWins([]);
    setTotalWin(0);
    (onUserDataChange ? Promise.resolve(onUserDataChange()) : Promise.resolve())
      .finally(() => {
        tokenAdjRef.current = { crewmate: 0, imposter: 0 };
        if (onTokenAdjustmentChange) onTokenAdjustmentChange({ crewmate: 0, imposter: 0, frozen: false });
        if (autoSpinRef.current) {
          setTimeout(() => handleSpinRef.current(), 500);
        }
      });
  }, [onUserDataChange, onTokenAdjustmentChange]);

  const renderFreeSpinsSummary = () => (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.92)', zIndex: 1000,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: 'linear-gradient(135deg, rgba(255,87,34,0.15), rgba(0,230,118,0.1))',
        borderRadius: '24px', padding: '36px 52px', border: '2px solid rgba(255,215,0,0.4)',
        textAlign: 'center', maxWidth: '420px',
        boxShadow: '0 0 60px rgba(255,215,0,0.15), 0 0 120px rgba(0,230,118,0.08)',
      }}>
        <div style={{ fontSize: '12px', fontWeight: '700', color: '#FF5722', letterSpacing: '3px', marginBottom: '8px' }}>
          EMERGENCY MEETING
        </div>
        <div style={{
          fontSize: '28px', fontWeight: '900', color: '#FFD700', marginBottom: '16px',
          textShadow: '0 0 20px rgba(255,215,0,0.4)',
        }}>
          FREE SPINS COMPLETE
        </div>
        <div style={{ fontSize: '14px', color: '#aaa', marginBottom: '8px' }}>
          You won
        </div>
        <div style={{
          fontSize: '40px', fontWeight: '900', color: '#00E676', margin: '4px 0',
          textShadow: '0 0 25px rgba(0,230,118,0.5)',
          animation: 'pulse 0.5s ease infinite alternate',
        }}>
          {fmtTokens(freeSpinTotal)} tokens
        </div>
        <div style={{ fontSize: '13px', color: '#888', marginTop: '8px', marginBottom: '20px' }}>
          in {bonusData?.totalSpins || bonusData?.spins || freeSpinIndex} free spins
          {bonusData?.multiplier > 1 ? ` at ${bonusData.multiplier}× multiplier` : ''}
        </div>
        <button
          onClick={dismissFreeSpins}
          style={{
            padding: '12px 36px', borderRadius: '12px',
            background: 'linear-gradient(135deg, #FF5722, #E91E63)',
            border: 'none', color: '#fff', fontSize: '14px', fontWeight: '800',
            cursor: 'pointer', letterSpacing: '2px', textTransform: 'uppercase',
            boxShadow: '0 0 20px rgba(255,87,34,0.3)',
            transition: 'transform 0.2s ease',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
        >
          COLLECT
        </button>
      </div>
    </div>
  );

  // ─── VENT MATCH-3 BONUS ───
  const VENT_MULTIPLIER_TIERS = [5, 10, 25, 50, 100, 250, 1000, 5000];
  const VENT_TIER_COLORS = {
    5: '#66BB6A', 10: '#42A5F5', 25: '#AB47BC', 50: '#FF7043',
    100: '#FF1744', 250: '#FFD600', 1000: '#FF6D00', 5000: '#E040FB',
  };

  const renderVentBonus = () => {
    if (!ventActive) return null;

    // Build tracker: group revealed cells by multiplier
    const multCounts = {};
    for (const val of Object.values(ventRevealedCells)) {
      if (val && val !== 'pending' && val.multiplier > 0) {
        multCounts[val.multiplier] = (multCounts[val.multiplier] || 0) + 1;
      }
    }
    // Find matched multiplier label
    const matchedMult = ventMatched !== null
      ? VENT_MULTIPLIER_TIERS[ventMatched] || 0
      : null;

    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.93)', zIndex: 1000,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        overflow: 'auto', padding: '10px',
      }}>
        <div style={{
          fontSize: '10px', fontWeight: '700', color: '#795548', letterSpacing: '3px',
          textTransform: 'uppercase', marginBottom: '4px',
        }}>VENT ESCAPE BONUS</div>
        <div style={{ fontSize: '20px', fontWeight: '900', color: '#FFD700', marginBottom: '4px' }}>
          Match 3 to Win!
        </div>
        <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '12px' }}>
          Reveal tiles — first multiplier to appear 3 times wins that prize
        </div>

        {/* Multiplier tracker */}
        <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
          {VENT_MULTIPLIER_TIERS.map(mult => {
            const count = multCounts[mult] || 0;
            if (count === 0) return null;
            const tierColor = VENT_TIER_COLORS[mult] || '#888';
            const isWinner = matchedMult === mult;
            return (
              <div key={mult} style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '3px 10px', borderRadius: '8px',
                background: isWinner ? 'rgba(255,215,0,0.3)' : 'rgba(255,255,255,0.08)',
                border: isWinner ? '1px solid #FFD700' : `1px solid ${tierColor}44`,
              }}>
                <span style={{ fontSize: '12px', color: tierColor, fontWeight: '800' }}>{mult}×</span>
                <span style={{ fontSize: '11px', color: '#fff', fontWeight: '700' }}>{count}/3</span>
              </div>
            );
          })}
        </div>

        {/* 9x9 grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(9, 1fr)',
          gap: '3px',
          maxWidth: '400px',
          width: '100%',
        }}>
          {Array.from({length: 9}).flatMap((_, r) =>
            Array.from({length: 9}).map((_, c) => {
              const key = `${r}-${c}`;
              const isRevealed = ventRevealed.has(key);
              const cellData = isRevealed ? ventRevealedCells[key] : null;
              const isPending = cellData === 'pending';
              const cellMult = (!isPending && cellData && cellData.multiplier > 0) ? cellData.multiplier : 0;
              const symbolId = (!isPending && cellData && cellData.symbolId >= 0) ? cellData.symbolId : -1;
              const isMatched = ventMatched !== null && symbolId === ventMatched && isRevealed && !isPending;
              const isDone = ventMatched !== null;
              const tierColor = cellMult > 0 ? (VENT_TIER_COLORS[cellMult] || '#888') : '#444';
              const isMiss = isRevealed && !isPending && symbolId === -1;

              return (
                <div
                  key={key}
                  onClick={() => !isDone && !isRevealed && handleVentReveal(r, c)}
                  style={{
                    width: '100%', aspectRatio: '1', borderRadius: '6px',
                    background: isPending
                      ? 'linear-gradient(135deg, rgba(121,85,72,0.4), rgba(80,50,30,0.5))'
                      : isMiss
                        ? 'rgba(40,40,40,0.5)'
                        : cellMult > 0
                          ? `${tierColor}18`
                          : 'linear-gradient(135deg, rgba(50,30,20,0.9), rgba(30,15,10,0.95))',
                    border: isMatched
                      ? `2px solid ${tierColor}`
                      : cellMult > 0
                        ? `1px solid ${tierColor}55`
                        : isMiss
                          ? '1px solid rgba(255,255,255,0.05)'
                          : '1px solid rgba(121,85,72,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: isDone || isRevealed ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    transform: isMatched ? 'scale(1.1)' : 'scale(1)',
                    boxShadow: isMatched ? `0 0 12px ${tierColor}88` : 'none',
                    animation: isMatched ? 'megaPulse 1s ease-in-out infinite' : 'none',
                    opacity: isDone && !isMatched ? 0.3 : 1,
                  }}
                  onMouseEnter={e => { if (!isDone && !isRevealed) e.currentTarget.style.transform = 'scale(1.12)'; }}
                  onMouseLeave={e => { if (!isDone && !isRevealed) e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  {isPending ? (
                    <span style={{ fontSize: '9px', color: '#795548' }}>...</span>
                  ) : cellMult > 0 ? (
                    <span style={{
                      fontSize: cellMult >= 1000 ? '7px' : cellMult >= 100 ? '8px' : '9px',
                      fontWeight: '900', color: tierColor,
                      textShadow: `0 0 6px ${tierColor}66`,
                    }}>{cellMult}×</span>
                  ) : isMiss ? (
                    <span style={{ fontSize: '10px', color: '#444' }}>✕</span>
                  ) : (
                    <VentSVG size={18} />
                  )}
                </div>
              );
            })
          )}
        </div>

        {ventMatched !== null && (
          <div style={{
            marginTop: '16px', textAlign: 'center',
            animation: 'megaPulse 1s ease-in-out infinite',
          }}>
            <div style={{ fontSize: '16px', color: VENT_TIER_COLORS[matchedMult] || '#FFD700', fontWeight: '800' }}>
              Matched 3 × {matchedMult}×!
            </div>
            <div style={{ fontSize: '24px', fontWeight: '900', color: '#00E676', marginTop: '4px' }}>
              Won {fmtTokens(ventPrize)} tokens!
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── TASK WHEEL BONUS ───
  const formatWheelLabel = (label) => {
    // Convert compact labels like "5k" to "5000", "2.5k" to "2500", etc.
    if (typeof label === 'string') {
      const match = label.match(/^([\d.]+)k[×xX]?$/i);
      if (match) return `${Math.round(parseFloat(match[1]) * 1000)}×`;
      const match2 = label.match(/^([\d.]+)[×xX]$/i);
      if (match2) return `${match2[1]}×`;
    }
    return label;
  };

  const renderTaskWheel = () => {
    const segments = bonusData?.segments || [];
    const numSegs = segments.length;
    const segAngle = 360 / numSegs;
    const wheelSize = 420;
    const half = wheelSize / 2;
    const radius = half - 10;

    // Vibrant alternating colors
    const segColors = [
      '#00E676', '#1A1A2E', '#FFD700', '#1A1A2E',
      '#FF5722', '#1A1A2E', '#26A69A', '#1A1A2E',
      '#E91E63', '#1A1A2E', '#2979FF', '#1A1A2E',
    ];

    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'radial-gradient(ellipse at center, rgba(38,166,154,0.15) 0%, rgba(0,0,0,0.95) 70%)',
        zIndex: 1000,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          fontSize: '12px', fontWeight: '700', color: '#26A69A', letterSpacing: '4px',
          textTransform: 'uppercase', marginBottom: '4px',
        }}>TASK COMPLETE</div>
        <div style={{
          fontSize: '32px', fontWeight: '900', color: '#FFD700', marginBottom: '24px',
          textShadow: '0 0 20px rgba(255,215,0,0.5), 0 0 40px rgba(255,215,0,0.2)',
          letterSpacing: '3px',
        }}>
          BONUS WHEEL
        </div>

        {/* Wheel with outer ring of lights */}
        <div style={{
          position: 'relative',
          width: `${wheelSize + 40}px`, height: `${wheelSize + 40}px`,
          marginBottom: '24px',
        }}>
          {/* Decorative light bulbs around the wheel */}
          {Array.from({ length: 24 }).map((_, i) => {
            const angle = (i / 24) * 360 - 90;
            const rad = (angle * Math.PI) / 180;
            const bulbR = (wheelSize + 40) / 2;
            const bx = bulbR + bulbR * 0.92 * Math.cos(rad);
            const by = bulbR + bulbR * 0.92 * Math.sin(rad);
            const lit = wheelSpinning ? (Math.floor(wheelAngle / 15) + i) % 3 === 0 : i % 2 === 0;
            return (
              <div key={i} style={{
                position: 'absolute',
                left: `${bx - 5}px`, top: `${by - 5}px`,
                width: '10px', height: '10px', borderRadius: '50%',
                background: lit ? '#FFD700' : '#444',
                boxShadow: lit ? '0 0 8px #FFD700, 0 0 16px rgba(255,215,0,0.4)' : 'none',
                transition: 'all 0.1s ease',
              }} />
            );
          })}

          {/* Pointer */}
          <div style={{
            position: 'absolute', top: '6px', left: '50%', transform: 'translateX(-50%)',
            width: 0, height: 0,
            borderLeft: '16px solid transparent', borderRight: '16px solid transparent',
            borderTop: '28px solid #FFD700', zIndex: 3,
            filter: 'drop-shadow(0 2px 8px rgba(255,215,0,0.6))',
          }} />

          {/* Wheel SVG */}
          <svg width={wheelSize} height={wheelSize} viewBox={`0 0 ${wheelSize} ${wheelSize}`}
            style={{
              position: 'absolute', top: '20px', left: '20px',
              transform: `rotate(${wheelAngle}deg)`,
              transition: wheelSpinning ? 'none' : 'transform 0.5s ease',
              filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.5))',
            }}>
            {/* Outer rim */}
            <circle cx={half} cy={half} r={radius + 4} fill="none" stroke="#FFD700" strokeWidth="3" opacity="0.6" />
            <circle cx={half} cy={half} r={radius} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1" />

            {segments.map((seg, i) => {
              const startAngle = i * segAngle - 90;
              const endAngle = startAngle + segAngle;
              const startRad = (startAngle * Math.PI) / 180;
              const endRad = (endAngle * Math.PI) / 180;
              const x1 = half + radius * Math.cos(startRad);
              const y1 = half + radius * Math.sin(startRad);
              const x2 = half + radius * Math.cos(endRad);
              const y2 = half + radius * Math.sin(endRad);
              const largeArc = segAngle > 180 ? 1 : 0;
              const midRad = ((startAngle + segAngle / 2) * Math.PI) / 180;
              const tx = half + (radius * 0.65) * Math.cos(midRad);
              const ty = half + (radius * 0.65) * Math.sin(midRad);
              const color = segColors[i % segColors.length];
              const isDark = color === '#1A1A2E';
              const displayLabel = formatWheelLabel(seg.label);

              return (
                <g key={i}>
                  <path
                    d={`M${half},${half} L${x1},${y1} A${radius},${radius} 0 ${largeArc},1 ${x2},${y2} Z`}
                    fill={color}
                    stroke="rgba(255,255,255,0.1)" strokeWidth="1"
                  />
                  <text x={tx} y={ty} textAnchor="middle" dominantBaseline="central"
                    fill={isDark ? '#FFD700' : '#fff'} fontSize={displayLabel.length > 5 ? '13' : '16'} fontWeight="900"
                    transform={`rotate(${startAngle + segAngle / 2 + 90}, ${tx}, ${ty})`}
                    style={{ fontFamily: 'Orbitron, monospace', textShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.5)' }}>
                    {displayLabel}
                  </text>
                </g>
              );
            })}
            {/* Center hub */}
            <circle cx={half} cy={half} r="35" fill="linear-gradient(#1a1028, #0a0514)" />
            <circle cx={half} cy={half} r="35" fill="#0a0514" />
            <circle cx={half} cy={half} r="33" fill="none" stroke="#FFD700" strokeWidth="3" />
            <circle cx={half} cy={half} r="28" fill="none" stroke="rgba(255,215,0,0.3)" strokeWidth="1" />
            <text x={half} y={half - 4} textAnchor="middle" dominantBaseline="central"
              fill="#FFD700" fontSize="11" fontWeight="900" style={{ fontFamily: 'Orbitron, monospace', letterSpacing: '2px' }}>
              TASK
            </text>
            <text x={half} y={half + 10} textAnchor="middle" dominantBaseline="central"
              fill="#26A69A" fontSize="8" fontWeight="700" style={{ fontFamily: 'Orbitron, monospace', letterSpacing: '1px' }}>
              BONUS
            </text>
          </svg>
        </div>

        {!wheelSpinning && !wheelResult && (
          <button onClick={handleWheelSpin} style={{
            padding: '16px 52px', borderRadius: '16px',
            background: 'linear-gradient(135deg, #FFD700, #FF9100)',
            border: 'none', color: '#1a1028', fontSize: '18px', fontWeight: '900',
            cursor: 'pointer', letterSpacing: '3px', textTransform: 'uppercase',
            boxShadow: '0 0 30px rgba(255,215,0,0.4), 0 4px 20px rgba(0,0,0,0.3)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            fontFamily: 'Orbitron, monospace',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 0 50px rgba(255,215,0,0.6), 0 6px 25px rgba(0,0,0,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 0 30px rgba(255,215,0,0.4), 0 4px 20px rgba(0,0,0,0.3)'; }}
          >
            SPIN
          </button>
        )}

        {wheelResult && (
          <div style={{
            padding: '20px 40px', borderRadius: '20px',
            background: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(0,230,118,0.1))',
            border: '2px solid rgba(255,215,0,0.4)',
            textAlign: 'center',
            animation: 'glow-border 1.5s ease infinite',
          }}>
            <div style={{ fontSize: '16px', color: '#FFD700', fontWeight: '800', letterSpacing: '3px' }}>YOU WON</div>
            <div style={{
              fontSize: '36px', fontWeight: '900', color: '#00E676', marginTop: '4px',
              textShadow: '0 0 20px rgba(0,230,118,0.5)',
            }}>
              {fmtTokens(wheelResult.prize)} tokens
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── PAYLINES OVERLAY ───
  const renderPaylinesOverlay = () => {
    if (!showPaylines) return null;
    return (
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 5, pointerEvents: 'none',
      }}>
        {PAYLINES.map((line, i) => (
          <svg key={i} style={{
            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
            opacity: 0.4,
          }} viewBox="0 0 500 300" preserveAspectRatio="none">
            <polyline
              points={line.map((row, reel) => `${reel * 100 + 50},${row * 100 + 50}`).join(' ')}
              fill="none" stroke={PAYLINE_COLORS[i]} strokeWidth="3"
              strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
        ))}
      </div>
    );
  };

  // ─── Win line highlight overlay ───
  const renderWinLineOverlay = () => {
    if (highlightedLine < 0 || !showWin) return null;
    const line = PAYLINES[highlightedLine];
    if (!line) return null;
    return (
      <svg style={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        zIndex: 4, pointerEvents: 'none',
      }} viewBox="0 0 500 300" preserveAspectRatio="none">
        <polyline
          points={line.map((row, reel) => `${reel * 100 + 50},${row * 100 + 50}`).join(' ')}
          fill="none" stroke={PAYLINE_COLORS[highlightedLine]} strokeWidth="5"
          strokeLinecap="round" strokeLinejoin="round"
          style={{ filter: `drop-shadow(0 0 6px ${PAYLINE_COLORS[highlightedLine]})` }}
        />
      </svg>
    );
  };

  // ─── RENDER ───
  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <button onClick={onBack} style={S.backBtn}>← Back</button>
        <div style={S.title}>🎰 Among Us Slots</div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setMuted(!muted)} style={{
          ...S.backBtn, fontSize: '16px', padding: '8px 12px',
        }}>
          {muted ? '🔇' : '🔊'}
        </button>
        <button onClick={() => setShowBonusBuy(true)} style={{
          ...S.backBtn, color: '#FF5722', borderColor: 'rgba(255,87,34,0.3)',
        }}>
          Buy Bonus
        </button>
        <button onClick={() => setShowPaytable(true)} style={{
          ...S.backBtn, color: '#FFD700', borderColor: 'rgba(255,215,0,0.3)',
        }}>
          Paytable
        </button>
      </div>

      {/* Machine */}
      <div style={S.machine}>
        {/* Balance bar */}
        <div style={S.balanceBar}>
          <div style={{ display: 'flex', gap: '16px' }}>
            <div>
              <span style={{ fontSize: '10px', color: '#666', letterSpacing: '1px' }}>BALANCE</span>
              <div style={{ fontSize: '16px', fontWeight: '800', color: tokenType === 'crewmate' ? '#3A8FD4' : '#E8384A' }}>
                {tokenType === 'crewmate' ? '🔵' : '🔴'} {fmtTokens(balance)}
              </div>
            </div>
          </div>
          <div>
            <span style={{ fontSize: '10px', color: '#666', letterSpacing: '1px' }}>BET</span>
            <div style={{ fontSize: '16px', fontWeight: '800', color: '#FFD700' }}>
              {fmtTokens(parseFloat(betAmount) || 0)}
            </div>
          </div>
          <div>
            <span style={{ fontSize: '10px', color: '#666', letterSpacing: '1px' }}>LINES</span>
            <div style={{ fontSize: '16px', fontWeight: '800', color: '#9B59B6' }}>{NUM_PAYLINES}</div>
          </div>
        </div>

        {/* Free spins HUD (above reels, during active bonus) */}
        {bonusMode === 'freeSpins' && freeSpinsStarted && renderFreeSpinsHUD()}

        {/* Reels */}
        <div style={S.reelsContainer}
          onMouseEnter={() => !spinning && setShowPaylines(true)}
          onMouseLeave={() => setShowPaylines(false)}
        >
          {renderPaylinesOverlay()}
          {renderWinLineOverlay()}

          {grid.map((col, reel) => {
            const isReelSpinning = !reelsStopped[reel];
            return (
              <div key={reel} style={S.reel(reelsStopped[reel])}>
                {col.map((sym, row) => {
                  const cellKey = `${reel}-${row}`;
                  const isBonusLanded = landedBonusCells.has(cellKey);
                  const isBonusSym = SCATTER_SYMBOLS.has(sym);
                  const isStoppedBonus = reelsStopped[reel] && isBonusSym;
                  const showBonusHighlight = isBonusLanded || isStoppedBonus;
                  return (
                    <div key={row} style={S.cell(
                      isCellWinning(reel, row),
                      isCellScatter(reel, row),
                      showBonusHighlight,
                      teaserActive && showBonusHighlight
                    )}>
                      <div style={{
                        animation: isStoppedBonus ? 'bonusSymbolLand 0.35s ease-out' : 'none',
                        filter: isReelSpinning && isBonusSym ? `drop-shadow(0 0 8px ${SYMBOLS.find(s => s.id === sym)?.color || '#FF5722'})` : 'none',
                      }}>
                        {renderSymbol(
                          sym,
                          48,
                          isCellWinning(reel, row) || isCellScatter(reel, row) || showBonusHighlight
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {/* Free spins overlay */}
          {/* Free spins HUD removed from inside reels — shown above instead */}
        </div>

        {/* Win banner */}
        {showWin && totalWin > 0 && (() => {
          const bet = parseFloat(betAmount) || 1;
          const winMult = totalWin / bet;
          const isEpic = winMult >= 100;
          const isMega = winMult >= 50;
          const isBig = winMult >= 25;
          const isNotable = winMult >= 10;
          return (
            <div style={{
              ...S.winBanner,
              ...(isEpic ? {
                background: 'linear-gradient(135deg, rgba(255,215,0,0.3), rgba(255,87,34,0.25), rgba(233,30,99,0.2))',
                border: '2px solid rgba(255,215,0,0.6)',
                animation: 'glow-border 1s ease infinite',
              } : isMega ? {
                background: 'linear-gradient(135deg, rgba(255,215,0,0.25), rgba(155,89,182,0.2))',
                border: '2px solid rgba(255,215,0,0.5)',
                animation: 'glow-border 1.5s ease infinite',
              } : isBig ? {
                background: 'linear-gradient(135deg, rgba(255,215,0,0.2), rgba(255,152,0,0.15))',
                border: '1px solid rgba(255,215,0,0.4)',
              } : {}),
            }}>
              <div style={{
                fontSize: isEpic ? '28px' : isMega ? '24px' : isBig ? '20px' : isNotable ? '18px' : '16px',
                fontWeight: '900', color: '#FFD700',
                textShadow: isEpic ? '0 0 30px rgba(255,215,0,0.8), 0 0 60px rgba(255,87,34,0.4)' :
                             isMega ? '0 0 20px rgba(255,215,0,0.6), 0 0 40px rgba(155,89,182,0.3)' :
                             isBig ? '0 0 15px rgba(255,215,0,0.5)' :
                             isNotable ? '0 0 10px rgba(255,215,0,0.3)' : 'none',
                animation: isEpic ? 'epicPulse 0.3s ease infinite alternate' :
                           isMega ? 'megaPulse 0.4s ease infinite alternate' :
                           isBig ? 'pulse 0.5s ease infinite alternate' : 'none',
              }}>
                {isEpic ? '💎 EPIC WIN!!! 💎' :
                 isMega ? '🎉 MEGA WIN! 🎉' :
                 isBig ? '🔥 BIG WIN! 🔥' :
                 isNotable ? '⭐ GREAT WIN! ⭐' : '✨ WIN!'}
              </div>
              <div style={{
                fontSize: isEpic ? '26px' : isMega ? '22px' : '20px',
                fontWeight: '900',
                color: isEpic ? '#FFD700' : '#00E676',
                textShadow: isEpic ? '0 0 20px rgba(255,215,0,0.6)' : isMega ? '0 0 10px rgba(0,230,118,0.4)' : 'none',
              }}>
                +{fmtTokens(totalWin)} {tokenType === 'crewmate' ? '🔵' : '🔴'}
              </div>
              {(isBig) && (
                <div style={{ fontSize: '12px', color: '#FFD700', marginTop: '4px', fontWeight: '700' }}>
                  {winMult.toFixed(1)}× your bet!
                </div>
              )}
              {wins.length > 0 && (
                <div style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>
                  Line {highlightedLine + 1}: {wins[winCycleIndex]?.count}× {SYMBOLS.find(s => s.id === wins[winCycleIndex]?.symbol)?.name || wins[winCycleIndex]?.symbol}
                </div>
              )}
            </div>
          );
        })()}

        {/* Controls */}
        <div style={S.controls}>
          {/* Token type */}
          <div style={{ display: 'flex', gap: '4px' }}>
            <button onClick={() => !spinning && setTokenType('crewmate')} style={S.tokenToggle(tokenType === 'crewmate')}>
              🔵 Crew
            </button>
            <button onClick={() => !spinning && setTokenType('imposter')} style={S.tokenToggle(tokenType === 'imposter')}>
              🔴 Imp
            </button>
          </div>

          {/* Bet amount */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button onClick={() => !spinning && setBetAmount(prev => Math.max(0.1, parseFloat((parseFloat(prev) / 2).toFixed(2))))}
              style={S.quickBet}>½</button>
            <input
              type="number"
              value={betAmount}
              onChange={e => setBetAmount(e.target.value)}
              onBlur={e => {
                const v = parseFloat(e.target.value);
                if (!Number.isFinite(v) || v < 0.1) setBetAmount(0.1);
                else setBetAmount(Math.min(10000, parseFloat(v.toFixed(2))));
              }}
              min="0.1"
              step="0.1"
              disabled={spinning}
              style={S.betInput}
            />
            <button onClick={() => !spinning && setBetAmount(prev => Math.min(10000, parseFloat((parseFloat(prev) * 2).toFixed(2))))}
              style={S.quickBet}>2×</button>
            <button onClick={() => !spinning && setBetAmount(Math.min(10000, parseFloat(balance.toFixed(2))))}
              style={{ ...S.quickBet, color: '#E8384A' }}>MAX</button>
          </div>

          {/* Spin button */}
          <button
            onClick={handleSpin}
            disabled={spinning || bonusMode !== null || bonusData !== null || buyingBonus}
            style={S.spinBtn(spinning || bonusMode !== null || bonusData !== null || buyingBonus)}
            onMouseEnter={e => { if (!spinning) e.currentTarget.style.transform = 'scale(1.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {spinning ? '...' : 'SPIN'}
          </button>

          {/* Auto-spin */}
          <button
            onClick={() => {
              if (autoSpin) {
                setAutoSpin(false);
              } else {
                setAutoSpin(true);
                if (!spinning) handleSpin();
              }
            }}
            style={{
              ...S.tokenToggle(autoSpin),
              borderColor: autoSpin ? 'rgba(0,230,118,0.6)' : 'rgba(255,255,255,0.1)',
              background: autoSpin ? 'rgba(0,230,118,0.2)' : 'rgba(255,255,255,0.05)',
              color: autoSpin ? '#00E676' : '#888',
            }}
          >
            {autoSpin ? '⏹ Stop' : '▶ Auto'}
          </button>
        </div>

        {/* Quick bet presets */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
          {[0.1, 0.5, 1, 5, 10, 25, 50, 100].map(v => (
            <button key={v} onClick={() => !spinning && setBetAmount(v)} style={{
              ...S.quickBet,
              background: parseFloat(betAmount) === v ? 'rgba(155,89,182,0.2)' : 'rgba(255,255,255,0.03)',
              borderColor: parseFloat(betAmount) === v ? 'rgba(155,89,182,0.4)' : 'rgba(255,255,255,0.06)',
              color: parseFloat(betAmount) === v ? '#9B59B6' : '#666',
            }}>{v}</button>
          ))}
        </div>
      </div>

      {/* History */}
      {history.length > 0 && (
        <div style={{
          maxWidth: '720px', width: '100%', marginTop: '16px',
          background: 'rgba(0,0,0,0.3)', borderRadius: '16px', padding: '12px',
          border: '1px solid rgba(255,255,255,0.05)',
        }}>
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#666', letterSpacing: '2px', marginBottom: '8px' }}>
            RECENT SPINS
          </div>
          <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', paddingBottom: '4px' }}>
            {history.slice(0, 10).map((h, i) => (
              <div key={i} style={{
                flexShrink: 0, padding: '6px 12px', borderRadius: '8px',
                background: h.bonus ? 'rgba(155,89,182,0.12)' : h.win > 0 ? 'rgba(0,230,118,0.1)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${h.bonus ? 'rgba(155,89,182,0.3)' : h.win > 0 ? 'rgba(0,230,118,0.2)' : 'rgba(255,255,255,0.05)'}`,
                fontSize: '11px', textAlign: 'center',
              }}>
                {h.bonus && <div style={{ fontSize: '9px', color: '#9B59B6', fontWeight: '700', marginBottom: '2px' }}>{h.bonus}</div>}
                <div style={{ color: h.win > 0 ? '#00E676' : '#666', fontWeight: '700' }}>
                  {h.win > 0 ? `+${fmtTokens(h.win)}` : h.bet > 0 ? `-${fmtTokens(h.bet)}` : '—'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Paytable modal */}
      {showPaytable && renderPaytable()}

      {/* Bonus Buy modal */}
      {showBonusBuy && renderBonusBuyMenu()}

      {/* Bonus overlays */}
      {bonusMode === 'freeSpins' && !freeSpinsStarted && renderFreeSpinsIntro()}
      {bonusMode === 'freeSpins' && freeSpinsComplete && renderFreeSpinsSummary()}
      {bonusMode === 'ventPick' && renderVentBonus()}
      {bonusMode === 'taskWheel' && renderTaskWheel()}

      {/* Pulse animation for big wins */}
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          100% { transform: scale(1.05); }
        }
        @keyframes megaPulse {
          0% { transform: scale(1); }
          100% { transform: scale(1.08); }
        }
        @keyframes epicPulse {
          0% { transform: scale(1) rotate(-1deg); }
          100% { transform: scale(1.12) rotate(1deg); }
        }
        @keyframes glow-border {
          0% { box-shadow: 0 0 5px rgba(255,215,0,0.3); }
          50% { box-shadow: 0 0 30px rgba(255,215,0,0.7), 0 0 60px rgba(255,87,34,0.3); }
          100% { box-shadow: 0 0 5px rgba(255,215,0,0.3); }
        }
        @keyframes epicShine {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes reelScroll {
          0% { transform: translateY(-40%); }
          100% { transform: translateY(40%); }
        }
        @keyframes bonusLand {
          0% { transform: scale(1.3); opacity: 0.6; }
          50% { transform: scale(0.9); opacity: 1; }
          75% { transform: scale(1.08); }
          100% { transform: scale(1); }
        }
        @keyframes bonusSymbolLand {
          0% { transform: scale(1.4) translateY(-10px); opacity: 0.5; }
          40% { transform: scale(0.85) translateY(3px); opacity: 1; }
          70% { transform: scale(1.1) translateY(-2px); }
          100% { transform: scale(1) translateY(0); }
        }
        @keyframes teaserPulse {
          0% { box-shadow: 0 0 15px rgba(255,152,0,0.4), inset 0 0 8px rgba(255,152,0,0.1); transform: scale(1); }
          50% { box-shadow: 0 0 35px rgba(255,152,0,0.8), 0 0 60px rgba(255,87,34,0.4), inset 0 0 15px rgba(255,152,0,0.25); transform: scale(1.04); }
          100% { box-shadow: 0 0 15px rgba(255,152,0,0.4), inset 0 0 8px rgba(255,152,0,0.1); transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

export default SlotsPage;
